/**
 * Milano Wigs — PDP Wizard Overlay v1.6.53
 *
 * CHANGE (v1.6.53): Discount Ninja price ownership.
 * - Trailing-zero cleanup is limited to wizard-owned option, summary, and
 *   review labels. It no longer traverses the PDP base/compare price, Discount
 *   Ninja money nodes, sticky product price, or cart bar price.
 * - This preserves DN's exact formatted output (including .00) and prevents
 *   competing text-node mutations while DN maintains its strikethrough pair.
 *
 * CHANGE (v1.6.52): Product-form utility-button safety.
 * - The cart-entry validation gate now ignores explicit type="button" and
 *   type="reset" controls, allowing Size guide, Compare, drawer, and modal
 *   triggers to work before the required personalization choice is made.
 * - Submit buttons and type-less HTML buttons remain gated.
 *
 * CHANGE (v1.6.51): Dynamic-label production hardening.
 * - Entry-label synchronization is idempotent: routine wizard updates no
 *   longer rewrite the radio's visible/accessibility text unless it changed.
 *
 * CHANGE (v1.6.50): Dynamic personalization entry label.
 * - The "Add …" summary now lists the visible step labels actually built from
 *   the current option set, in wizard order, instead of naming fixed categories.
 * - Missing categories are omitted and Ear-Pieces-only sets inherit the
 *   contextual "Ear Pieces" label established in v1.6.49.
 * - It re-syncs with the existing pill navigation when length conditions hide
 *   or reveal an entire category.
 *
 * CHANGE (v1.6.49): Ear-pieces-only Secure stage.
 * - When Add Ear Pieces is the only option classified into Secure, the pill,
 *   heading, description, and help link use the existing Ear Pieces language.
 * - The duplicate in-row add-on heading is suppressed for that narrow case.
 * - Detection is based on the rendered SC option rows, not product names or
 *   option-set IDs; products with any real Secure Method keep existing copy.
 *
 * CHANGE (v1.6.48): Style default copy polish.
 * - The injected Style / Part Location default now reads "Keep As Pictured"
 *   so its capitalization matches the surrounding option labels.
 *
 * CHANGE (v1.6.47): Grace per-length (band) conditional SC option groups.
 * Consolidated Grace products price some SC options by Length BAND
 * (Short/Mid = 12/13/16/19/22" vs Long/X-Long = 24/26" — boundary corrected
 * 2026-06-10 from the actual SC sets); SC can't branch on
 * a native variant, so the production set carries BOTH band versions of any
 * group whose price differs (today: Color — "Add Color Service"/"Color
 * Services" vs "Color Services - Processed") and the new
 * applyLengthVisibility() shows only the band matching the selected Length.
 * - Hiding a banded row also CLEARS its selections: SC serializes CHECKED
 *   inputs to line-item properties/prices regardless of CSS visibility, so
 *   a class toggle alone would still charge/submit the hidden group.
 * - Banded pairs FAIL SAFE: if the Length can't be resolved, the whole pair
 *   hides (a shopper must never see the wrong band's price). Everything
 *   else FAILS OPEN: rows not in a banded pair — including SC's own unnamed
 *   conditional shells — are never touched.
 * - data-mw-length-hidden marks OUR hides, layered on the same
 *   milano-option-row--hidden class options.js's SC-condition watcher owns;
 *   we never un-hide a row SC itself is hiding.
 * - Re-applies: transform build, native variant change (capture listener +
 *   doVariantWork), SC re-renders (change debounce + bootstrap registry),
 *   and a final pre-submit enforcement inside prepareCartSubmitPayload.
 * - Steps left with zero visible rows drop out of the pill nav and are
 *   skipped by goToStep (direction-aware); the Continue→Review boundary
 *   moves to the last visible step. Virtual skip cards re-host if their
 *   host row gets length-hidden.
 * - autoSelectDefaults/autoSelectRecommended now skip hidden rows so a
 *   hidden group can't be re-defaulted into the cart payload.
 * - reclassifyDynamicRows: SC's conditional sub-groups (Position of Face
 *   Framing / Bangs or Fringe) render as unnamed shells and used to land in
 *   the Style step by default; once SC reveals + renames them they are
 *   re-filed into the step their real name classifies to — the dropdown now
 *   appears INLINE on the Cut screen, directly under the Cut group.
 * - Perf micro-pass (INP/main-thread): renderIndicator memoizes its output
 *   (no innerHTML rebuild / pill replacement when unchanged — also keeps
 *   keyboard focus); goToStep's enter-animation restarts with ONE batched
 *   reflow instead of one forced layout per row; the synchronous capture-
 *   phase re-band runs only for Length inputs (Lace/native clicks skip it);
 *   single-fieldset fast path avoids getComputedStyle walks per change.
 * - setupColorExclusivity generalized to ALL color groups (the union set
 *   renders two) via one delegated, idempotent, re-render-proof listener
 *   per group; re-called from the change debounce.
 * - Band-change re-defaults are SCOPED to just-reshown rows (a global
 *   defaults pass would re-impose "keep as pictured" on unrelated groups
 *   the shopper emptied on purpose); cleared-input jQuery notifications are
 *   batched + deferred 150ms (clearAllWizardSelections pattern); bfcache
 *   pageshow re-applies (browsers restore form state).
 * Scoped to body.milano-grace + milano-wizard-mode; non-Grace PDPs and
 * flat-options/shop-by-looks PDPs are untouched.
 *
 * CHANGE (v1.6.46): Cart-submit hardening. Adds a lightweight
 * final pre-submit validation/preparation pass that keeps SC Options form
 * attributes aligned, prevents missing entry-choice submits, preserves base
 * variant selections, and clears stale wizard-only inputs on as-pictured
 * submits without touching Cap/Lace setup selections.
 *
 * CHANGE (v1.6.45): Cap Size SC Options isolation.
 * - watchVariantChanges now filters the shared mutation pipeline to native
 *   Shopify variant DOM only. SC Options rows such as Cap Size no longer
 *   trigger the variant clean-up/reorder/dim cycle, so Cap clicks behave like
 *   other wizard SC Options selections instead of causing a PDP re-render flash.
 * - Removed the price-block dimming workaround. Splitit is now stabilized by
 *   options.js as a persistent row, so masking the price area is unnecessary.
 *
 * CHANGE (v1.6.44): Variant-click re-render flash mitigation.
 * - Theme's renderProductInfo() swaps innerHTML on .product__price-and-badge
 *   on variant change, briefly tearing down + recreating .milano-splitit-
 *   fallback and the (+$X services) context span. Without coverage, the
 *   result is the visible "splitit disappears + reappears" + adjacent
 *   layout flash Kevin reported on desktop when clicking length / cap /
 *   lace variants.
 * - FIX: watchVariantChanges.hideSelectorsOnMutation now also dims
 *   .product__price-and-badge to 0.35 opacity during the 200ms mutation
 *   window, then restores to 1 in doVariantWork. Subtle enough to keep
 *   the price layout visible as a reference, opaque enough to mask the
 *   inner content swap.
 *
 * CHANGE (v1.6.43): reorderForGrace robustness + viewport unification.
 * - VIEWPORT UNIFICATION: desktop now matches mobile order \u2014 Length \u2192 Cap \u2192
 *   Lace on both. v1.6.41 had deliberately put Cap before Length on
 *   desktop; per Kevin 2026-05-26, that's now wrong. Removed the
 *   isDesktop branching + the (now-superfluous) idempotent-check fork.
 * - MID-RENDER DEFERRAL: theme/Globo re-renders fieldsets on variant
 *   click. During that swap, OLD and NEW length fieldsets briefly
 *   co-exist in the DOM, producing the "duplicate Length flashes
 *   underneath" Kevin reported. reorderForGrace now bails when
 *   querySelectorAll finds more than one length fieldset \u2014 the next
 *   mutation cycle (after the OLD is removed) does the reorder cleanly.
 * - The resize listener that called reorderForGrace on viewport change
 *   is retained but is now effectively a no-op for the ordering (since
 *   order is viewport-independent). Kept because it's harmless and
 *   protects against future Grace layout work that DOES need viewport
 *   awareness.
 *
 * CHANGE (v1.6.42): Patch + recommended next-passes from 2026-05-26 review.
 * - COPY: buildPersonalizeDesc rewritten. Desktop leads with the
 *   ships-as-pictured promise then invites adjustment. Mobile condensed
 *   to one line at iPhone 14 Pro Max width.
 * - BUG: removed two `helperText` ReferenceErrors in autoSelectCapSize
 *   that survived from v1.6.11's cap-helper era. `.mw-cap-helper` element
 *   was deleted in v1.6.13; both `if` guards always fell through, but the
 *   reads would have thrown if the element ever returned. Dead code gone.
 * - A11Y: aria-current="step" added to active step pill in renderIndicator;
 *   aria-live="polite" + aria-atomic="true" on .mw-hint so step-change
 *   copy is announced to screen readers.
 * - PERF / NOISE: introduced DEBUG flag + log() helper. Every operational
 *   console.log routed through log() — silent in prod, restorable by
 *   flipping `DEBUG = true`. Version banner stays as console.log so
 *   we can verify deploys via DevTools.
 * - SIMPLIFICATION: STEP_REASSURANCE config + .mw-step-notice element +
 *   updateStepNotice function + 2 call sites + ~15 lines of injected CSS
 *   all removed. The review-card reminder now triggers off the much
 *   simpler `getServicesDollars() > 0` check with a generic message —
 *   same conversion signal, ~100 LOC lighter.
 * - A11Y: .mw-review-card__edit changed from <a href="#"> to <button>.
 *   Eliminates the hash-jump artifact (click was scrolling viewport to
 *   top before goToStep ran). CSS already styled it as a text-link so
 *   visual is identical.
 *
 * CHANGE (v1.6.35): Major perf + FOUC + Grace launch readiness pass.
 *
 * Reliability fixes:
 * - autoSelectCapSize: returns boolean now, retries via bootstrap registry
 *   until cap radios are painted AND a selection sticks. Fixes the fresh-
 *   Grace-PDP "no cap selected" bug. Also re-runs from watchVariantChanges
 *   so Globo re-renders can't wipe the auto-selection. Early-returns if
 *   user has already chosen, so it never overrides intent.
 * - reorderForGrace: new function moves Length above Cap Size on Grace
 *   PDPs (final order: Length → Cap → Lace). Registered with bootstrap +
 *   re-runs from watchVariantChanges so Globo re-render can't undo it.
 *   Idempotent via compareDocumentPosition check.
 *
 * Performance:
 * - bootstrapGraceHelpLinks routed through unified MILANO_addBootstrap
 *   registry (options.js owns the registry). 6 setIntervals across both
 *   files consolidated to 1 shared interval.
 *
 * FOUC prevention:
 * - body.milano-grace assigned at IIFE start (not in init) — as early as
 *   the script can run. Paired with new CSS rule that hides the Length
 *   fieldset until body.milano-wizard-ready is set.
 * - milano-wizard-ready set inside tryTransform on success via rAF (so
 *   reveal paints in the same frame as the wizard layout — no flash).
 * - 3-second IIFE-level setTimeout adds milano-wizard-ready unconditionally
 *   so a failed transform can't leave the Length fieldset permanently
 *   hidden. Defense in depth.
 *
 * (Earlier changelog entries retained for historical context — see prior
 * versions in git for full history.)
 */

(function() {
  'use strict';

  if (window.MilanoWizardInit) return;
  window.MilanoWizardInit = true;

  var VERSION = 'v1.6.53';
  // ===========================================================================
  // DEBUG FLAG (v1.6.42)
  //
  // Operational console output routes through log() — no-op in production.
  // Flip DEBUG = true to surface deploy/version details during dev debugging.
  // console.warn / console.error remain — those signal real conditions and
  // should always surface.
  // ===========================================================================
  var DEBUG = !!window.MILANO_DEBUG;
  function log() {
    if ((DEBUG || window.MILANO_DEBUG) && window.console && console.log) {
      console.log.apply(console, arguments);
    }
  }

  if (window.location.pathname.indexOf('/products/') === -1) return;

  // ===========================================================================
  // EARLY BODY CLASS ASSIGNMENT (FOUC prevention) — v1.6.35
  // ===========================================================================
  function applyEarlyGraceClass() {
    if (!document.body) return;
    var path = window.location.pathname.toLowerCase();
    if (path.indexOf('/products/grace') !== -1) {
      document.body.classList.add('milano-grace');
    }
  }
  if (document.body) {
    applyEarlyGraceClass();
  } else {
    document.addEventListener('DOMContentLoaded', applyEarlyGraceClass);
  }
  setTimeout(function() {
    if (document.body) document.body.classList.add('milano-wizard-ready');
  }, 3000);

  // =========================================================================
  // CONFIG
  // =========================================================================

  var STEP_DEFS = [
    { id: 0, label: 'Secure', patterns: [/secure method/i, /ear piece/i, /blending.*hairline/i, /front hairline/i], hint: 'How should we secure your wig?', subhint: 'Choose how your wig stays comfortably in place.', helpId: 'secure-method', helpText: 'See options' },
    { id: 1, label: 'Color', patterns: [/color service/i, /add color/i], hint: 'Would you like color services?', subhint: 'Add a natural-looking root, highlights, or custom blend.', helpId: 'color-service', helpText: 'See color examples' },
    { id: 2, label: 'Cut', patterns: [/personalize your cut/i, /choose your cut/i, /signature.*cut/i, /face fram/i, /bangs or fringe/i, /position of/i], hint: 'Would you like a custom cut?', subhint: 'Our stylists shape each wig by hand to frame your face.', helpId: 'milano-signature', helpText: 'See cut options' },
    { id: 3, label: 'Style', patterns: [/style my/i, /please style/i, /location of part/i], hint: 'How should we style it?', subhint: 'Your preferred styling and part location.', helpId: null, helpText: null }
  ];

  var SECURE_POPUP_MAP = [
    { pattern: /onyx/i,                popup: 'onyx-secure-method' },
    { pattern: /diamond/i,             popup: 'diamond-secure-method' },
    { pattern: /topaz|emerald/i,       popup: 'topeme-secure-method' },
    { pattern: /\btopper(?!s\b)/i,     popup: 'dtoppers-secure-method' }
  ];

  var STYLE_POPUP_MAP = [
    { pattern: /wavy/i, popup: 'please-style-my-wig-wavy' }
  ];

  var CUT_POPUP_MAP = [
    { pattern: /signature.*cut/i, popup: 'milano-signature' }
  ];

  var LACE_POPUP_MAP = [
    { pattern: /ponytail/i, popup: 'lace-compare-ponytail' },
    { pattern: /divine/i,   popup: 'lace-compare-divine' }
  ];

  var INLINE_HELP_MAP = [
    { nameMatch: 'position of', popup: 'face-farming-position', label: 'Where it ends' },
    { nameMatch: 'location of part', popup: 'location-of-part', label: 'Part options' },
    { nameMatch: 'bangs or fringe', popup: 'bangs-or-fringe', label: 'Bang styles' },
    { nameMatch: 'style my', popup: 'please-style-my-wig', label: 'Style examples' },
    { nameMatch: 'please style', popup: 'please-style-my-wig', label: 'Style examples' }
  ];

  // Keys are the option title lowercased with runs of whitespace collapsed (see
  // injectLaceSubtitles). The "... Front" and misspelled entries are not new
  // copy — they are the same two descriptions reached by label spellings the SC
  // data actually ships, which previously fell through and rendered no
  // description at all. Fix the labels in admin and these keys go quiet on
  // their own; they are deliberately harmless to leave in place.
  var LACE_SUBS = {
    'zig zag standard lace': 'Flatter look, great for bangs',
    'zig zag lace': 'Flatter look, great for bangs',
    'zig zag lace front': 'Flatter look, great for bangs',
    'zig zag standarad lace': 'Flatter look, great for bangs',
    'standard lace': 'Flatter look, great for bangs',
    'standard': 'Flatter look, great for bangs',
    'zig zag deep lace': 'More volume, ideal for side parts',
    'zig zag deep lace front': 'More volume, ideal for side parts',
    'zig zig deep lace': 'More volume, ideal for side parts',
    'deep lace': 'More volume, ideal for side parts',
    'deep lace front': 'More volume, ideal for side parts'
  };

  // Ponytail-only Deep Lace copy (Kevin, 2026-08-11). Ponytail PDPs get their
  // own sub-label under Deep Lace; every other line keeps the shared LACE_SUBS
  // wording. Keyed and looked up exactly like LACE_SUBS (lowercased option
  // title), and it covers each Deep Lace spelling that actually ships on a
  // ponytail product — the SC data carries a few one-offs ("... Front", and a
  // "Zig Zig" typo) that the shared map never had keys for. Only Deep Lace is
  // overridden, so the sibling "Zig Zag Lace" line falls through unchanged.
  var PONYTAIL_DEEP_LACE_SUB = 'More volume, ideal for those who prefer a further-back transition';

  var LACE_SUBS_PONYTAIL = {
    'zig zag deep lace': PONYTAIL_DEEP_LACE_SUB,
    'zig zag deep lace front': PONYTAIL_DEEP_LACE_SUB,
    'zig zig deep lace': PONYTAIL_DEEP_LACE_SUB,
    'deep lace': PONYTAIL_DEEP_LACE_SUB,
    'deep lace front': PONYTAIL_DEEP_LACE_SUB
  };

  var LABEL_RENAMES = {
    'no color service': 'No Color \u2014 keep as pictured',
    'keep original color': 'No Color \u2014 keep as pictured',
    'as is': 'No Cut \u2014 keep as pictured'
  };

  // =========================================================================
  // "KEEP AS PICTURED" COPY — single source of truth (v1.6.4)
  // =========================================================================

  var SKIP_COPY = {
    cut: {
      virtualLabel: 'Keep cut as pictured',
      sepLabel: 'Or choose a cut:',
      reviewLabel: 'As pictured'
    },
    color: {
      virtualLabel: 'Keep color as pictured',
      sepLabel: 'Or add color services:',
      reviewLabel: 'As pictured'
    }
  };

  var VIRTUAL_SKIP_CONFIGS = {
    cut: {
      stepId: 2,
      nativeClass: 'mw-no-cut',
      virtualClass: 'mw-no-cut-virtual',
      sepClass: 'mw-cut-sep',
      skipSubgates: true,
      flag: 'virtualCutSelected',
      copy: SKIP_COPY.cut
    },
    color: {
      stepId: 1,
      nativeClass: 'mw-no-color',
      virtualClass: 'mw-no-color-virtual',
      sepClass: 'mw-color-sep',
      skipSubgates: false,
      flag: 'virtualColorSelected',
      copy: SKIP_COPY.color
    }
  };

  // v1.6.42: STEP_REASSURANCE removed. The per-step .mw-step-notice element +
  // injection were over-engineered for what amounted to one sentence shown
  // when paid Color/Cut options were selected. Replaced with a single
  // generic reminder line at the bottom of the review card, triggered off
  // getServicesDollars() > 0. Same conversion signal, ~100 LOC lighter.
  //
  // v2026-06-05: RESTORED (inline-only). The per-step reassurance returns as the
  // inline .mw-step-notice card, shown when a PAID option is selected on the
  // Color (step 1) or Cut (step 2) step — matching the live theme. The generic
  // review-card reminder is removed (see updateReviewCard) so the message shows
  // once, in context. Keys are wizard step IDs (0 Secure, 1 Color, 2 Cut, 3 Style).
  var STEP_REASSURANCE = {
    1: {
      lead: 'We’ll bring your vision to life.',
      body: 'After your order, our team will email you to discuss your color preferences — reference photos welcome.'
    },
    2: {
      lead: 'We’ll shape it just for you.',
      body: 'After your order, our team will email you to discuss your desired cut and face shape.'
    }
  };

  var ADDON_HEADERS = [
    {
      nameMatch: 'ear piece',
      title: 'Add Ear Pieces',
      desc: 'Small hair sections near the ears for seamless coverage.',
      helpId: 'ear-pieces',
      helpText: 'Learn more',
      warm: false
    },
    {
      nameMatch: 'hairline',
      altMatch: 'blending',
      title: 'About your front hairline',
      desc: 'This helps us prepare your topper for the most natural-looking result.',
      helpId: 'front-hairline',
      helpText: 'Learn more',
      warm: true
    }
  ];

  var CAP_SIZE_MAP = {
    'princess': { size: 'small' },
    'processed euro': { size: 'large' },
    'processed-euro': { size: 'large' }
  };

  // =========================================================================
  // GRACE LENGTH BANDS (v1.6.47)
  //
  // Pricing on consolidated Grace products is banded by Length. Band is
  // resolved from the checked Length radio's value — leading inches first
  // (most stable), then line name. Unknown length → null → banded pairs
  // fail SAFE (every member hidden).
  //
  // Per-length option banding: groups whose SC config (price/values) differs by
  // length band. The rules live in lengthBandDecision() below (suffix-driven, 3
  // bands). Groups matching no band (uniform: no suffix, no variant siblings) are
  // never touched. To band a new group later, just create "<base> - <Band>" in SC.
  // =========================================================================

  // Band boundary per Kevin's check of the actual SC sets (2026-06-10):
  // Short/Mid = 12/13/16/19/22" — Long/X-Long = 24/26" ONLY.
  // (22" Reese was initially mis-reported as long; the sets say short/mid.)
  var LENGTH_BAND_BY_INCHES = {
    12: 'short_mid', 13: 'short_mid', 16: 'short_mid', 19: 'short_mid', 22: 'short_mid',
    24: 'long', 26: 'x_long'
  };

  var LENGTH_BAND_BY_NAME = {
    'halle': 'short_mid', 'victoria': 'short_mid', 'naomi': 'short_mid', 'nicole': 'short_mid', 'reese': 'short_mid',
    'gisele': 'long', 'amber': 'x_long'
  };

  // Group-name normalizer for the "- Processed" twin convention below.
  function mwNormalizeOptionName(name) {
    return (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  // --- Generic per-band group banding (3 bands: short_mid / long / x_long) ---
  // A group's band is read from a NAME SUFFIX: "<base> - Short/Mid" /
  // " - Processed" (or " - Long") / " - X-Long". A bare group with no suffix is
  // a BASE: it shows on every band that has no sibling variant (e.g. one shared
  // "Wig Secure Method" + a "Wig Secure Method - X-Long" free override for 26").
  // Color is always band-specific (a bare "Color Services" defaults to short_mid).
  // Uniform groups (no suffix, no variant siblings) are never touched.
  // X-Long INHERITS Long: a 26" length uses the Long groups UNLESS a 26"-specific
  // "- X-Long" override exists — so SC overrides can be added with zero risk and
  // existing 2-band sets keep working unchanged. To band any new group later,
  // just create "<base> - <Band>" in SC. No code change.

  // Read the band from the group's name. X-Long is checked first because it
  // contains "long". Note "Long/X-Long" reads as x_long, so a Long-only group
  // must say "- Long" (not "- Long/X-Long"); descriptive text can precede the
  // trailing band token.
  function bandFromSuffix(name) {
    if (/x[\s_\-]?long/i.test(name)) return 'x_long';
    if (/processed/i.test(name) || /\blong\b/i.test(name)) return 'long';
    if (/short[\s_\-\/]?mid/i.test(name)) return 'short_mid';
    return null;
  }
  // Band of the token that trails a normalized family base.
  function mwBandTokenToBand(tok) {
    if (tok.indexOf('xlong') > -1) return 'x_long';
    if (tok.indexOf('processed') > -1 || tok.indexOf('long') === 0) return 'long';
    if (tok.indexOf('shortmid') > -1) return 'short_mid';
    return null;
  }
  // Bands that have a sibling variant of this base (excludes the base itself).
  function mwCoveredBands(base, normSiblings) {
    var out = [];
    if (!base || !normSiblings) return out;
    for (var i = 0; i < normSiblings.length; i++) {
      var other = normSiblings[i];
      if (other === base || other.indexOf(base) !== 0) continue;
      var b = mwBandTokenToBand(other.slice(base.length));
      if (b && out.indexOf(b) === -1) out.push(b);
    }
    return out;
  }

  // Visibility decision for one option group on the current length band:
  //   null  = not banded (never touch)
  //   true  = hide (+ clear) on this band
  //   false = show on this band
  function lengthBandDecision(name, normSiblings, currentBand) {
    var isColor = /color service/i.test(name);
    var sb = bandFromSuffix(name);

    if (sb || isColor) {                                  // band-specific VARIANT
      var vb = sb || 'short_mid';                         // bare color = short_mid
      if (currentBand === null) return true;              // fail-safe: hide variants
      return vb !== currentBand;                          // show iff this is its band
    }

    var base2 = mwNormalizeOptionName(name);              // BASE (charged) or uniform
    var covered = mwCoveredBands(base2, normSiblings);
    if (!covered.length) return null;                     // uniform -> fail open (always shown)
    if (currentBand === null) return false;               // base = safe default
    return covered.indexOf(currentBand) > -1;             // hide base where a "<base> - <Band>" override exists
  }

  // =========================================================================
  // STATE
  // =========================================================================

  var wiz = {
    active: false,
    currentStep: 0,
    steps: [],
    reviewed: false,
    indicatorEl: null,
    navEl: null,
    hintEl: null,
    subhintEl: null,
    toggleTotalEl: null,
    wizTotalEl: null,
    reviewCardEl: null,
    segmentedEl: null,
    entryChoice: null,
    entryGateInstalled: false,
    personalizeDefaultsApplied: false,
    entryErrorEl: null,
    syncingEntryCheckbox: false,
    personalizeDescEl: null,
    intervals: [],
    observers: [],
    collectionSignal: '',
    isTopper: false,
    isAccessory: false,
    virtualCutSelected: false,
    virtualColorSelected: false
  };

  // =========================================================================
  // HELPERS
  // =========================================================================

  function stripPriceText(text) {
    return (text || '').replace(/\s*\+?\$[\d,.]+\s*(usd)?/gi, '').trim().toLowerCase();
  }

  // Shared "keep as pictured" label predicate — used by autoSelectDefaults
  // (global pass) and applySkipDefaultToRow (scoped band-change pass).
  function isSkipDefaultLabel(text) {
    return text.indexOf('as is') > -1 ||
           text.indexOf('as-pictured') > -1 ||
           text.indexOf('no cut') > -1 ||
           text.indexOf('no color') > -1 ||
           text.indexOf('keep as pictured') > -1 ||
           text.indexOf('keep original') > -1;
  }


  function requestPDPPriceSync(reason) {
    if (typeof window.MILANO_schedulePDPPriceSync === 'function') {
      window.MILANO_schedulePDPPriceSync(reason || 'wizard', { delay: 80, verify: true });
    } else if (typeof window.MILANO_syncPDPPrice === 'function') {
      window.MILANO_syncPDPPrice(reason || 'wizard');
    }
  }

  function getServicesDollars() {
    var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
    var personalizeEnabled = !checkbox || checkbox.checked;

    // personalize-options.js owns the production price calculation because it
    // already watches SC Options, native variant price, cart bar, Splitit, and
    // Klarna. Use that single source of truth when available so the wizard
    // context survives native Length/Lace renders.
    if (typeof window.MILANO_getSelectedServiceCents === 'function') {
      var serviceCents = parseInt(window.MILANO_getSelectedServiceCents() || 0, 10);
      return serviceCents > 50 ? Math.round(serviceCents / 100) : 0;
    }

    // Fallback for the first few hundred ms before personalize-options.js starts:
    // sum the checked SC Options value prices directly instead of relying only
    // on the app's rendered total, which Palo Alto can temporarily reset on
    // native variant changes.
    // Mirror the canonical getSelectedServiceCents() de-dupe: ONE price per
    // single-select (radio) group, so a stale + live Cap Size widget can't
    // double-count to +$200 during the brief window before personalize-options.js
    // takes over. Checkbox groups still sum distinct values.
    var checkboxCents = 0;
    var seen = [];
    var radioByGroup = {};
    var checked = document.querySelectorAll('.bold_option input:checked');
    for (var i = 0; i < checked.length; i++) {
      var input = checked[i];
      if (input.disabled) continue;
      var row = input.closest('.bold_option');
      if (!row) continue;
      if (!personalizeEnabled && !row.classList.contains('milano-always-visible')) continue;
      var valueEl = input.closest('.bold_option_value') || input.closest('label') || row;
      var priceEl = valueEl ? valueEl.querySelector('.bold_option_value_price') : null;
      if (!priceEl) continue;
      var cents = Math.round((parseFloat((priceEl.textContent || '').replace(/,/g, '').replace(/[^\d.]/g, '')) || 0) * 100);
      if (cents <= 0) continue;
      var groupKey = ((row.getAttribute('data-option-name') || '').trim().toLowerCase()) || (input.name || '') || ('__row-' + i);
      if ((input.type || '').toLowerCase() === 'radio') {
        var shown = !!(input.getClientRects && input.getClientRects().length > 0);
        var prev = radioByGroup[groupKey];
        if (!prev || shown || !prev.shown) radioByGroup[groupKey] = { cents: cents, shown: shown };
      } else {
        var key = groupKey + '|' + (input.value || '');
        if (seen.indexOf(key) > -1) continue;
        seen.push(key);
        checkboxCents += cents;
      }
    }
    var selectedCents = checkboxCents;
    for (var rg in radioByGroup) { if (radioByGroup.hasOwnProperty(rg)) selectedCents += radioByGroup[rg].cents; }
    if (selectedCents > 50) return Math.round(selectedCents / 100);

    var fw = document.querySelector('product-form[data-form-wrapper]');
    if (fw) {
      var baseCents = parseInt(fw.getAttribute('data-variant-price') || '0', 10);
      var scEl = document.querySelector('[data-product-price].bold_option_price_display, .bold_option_price_display [data-product-price]');
      if (scEl) {
        var scText = (scEl.textContent || '').replace(/[^\d.]/g, '');
        var scCents = Math.round((parseFloat(scText) || 0) * 100);
        var added = scCents - baseCents;
        if (added > 50) return Math.round(added / 100);
      }
    }

    var summaryTotal = document.querySelector('.milano-summary__total:not(.milano-summary__options-total) span:last-child');
    if (summaryTotal) {
      var st = (summaryTotal.textContent || '').replace(/[^\d.]/g, '');
      var summaryDollars = parseFloat(st) || 0;
      return Math.round(summaryDollars);
    }

    return 0;
  }

  function walkUp(el, cls) {
    var node = el ? el.parentElement : null;
    while (node && !node.classList.contains(cls)) node = node.parentElement;
    return node;
  }

  function detectCollection() {
    var collectionEls = document.querySelectorAll('[data-collection-handle], .breadcrumb a, .breadcrumbs a');
    var pageUrl = window.location.pathname.toLowerCase();
    var productTitle = (document.querySelector('.product__title, h1') || {}).textContent || '';
    var combined = pageUrl + ' ' + productTitle.toLowerCase();

    for (var c = 0; c < collectionEls.length; c++) {
      combined += ' ' + (collectionEls[c].getAttribute('href') || '') + ' ' + (collectionEls[c].textContent || '');
    }

    if (document.referrer) combined += ' ' + document.referrer.toLowerCase();

    for (var key in CAP_SIZE_MAP) {
      if (combined.indexOf(key) > -1) return key;
    }
    return null;
  }

  function getCollectionSignal(step) {
    var parts = [];
    if (step && step.rows) {
      for (var r = 0; r < step.rows.length; r++) {
        parts.push(step.rows[r].getAttribute('data-option-name') || '');
      }
    }

    if (!wiz.collectionSignal) {
      var pageParts = [];
      pageParts.push(window.location.pathname);
      pageParts.push((document.querySelector('.product__title, h1') || {}).textContent || '');
      wiz.collectionSignal = pageParts.join(' ').toLowerCase();
    }

    return (parts.join(' ') + ' ' + wiz.collectionSignal).toLowerCase();
  }

  function resolveStepHelpId(step) {
    var defaultId = step.helpId || null;
    if (!defaultId) return null;

    var map = null;
    if (step.id === 0) map = SECURE_POPUP_MAP;
    else if (step.id === 2) map = CUT_POPUP_MAP;
    else if (step.id === 3) map = STYLE_POPUP_MAP;

    if (!map) {
      return document.getElementById(defaultId) ? defaultId : null;
    }

    var signal = getCollectionSignal(step);

    for (var i = 0; i < map.length; i++) {
      if (map[i].pattern.test(signal) && document.getElementById(map[i].popup)) {
        return map[i].popup;
      }
    }

    return document.getElementById(defaultId) ? defaultId : null;
  }

  // =========================================================================
  // INIT
  // =========================================================================

  var _scrollFixInstalled = false;

  function initScrollFix() {
    if (_scrollFixInstalled) return;
    _scrollFixInstalled = true;

    function isAnythingOpen() {
      var cl = document.body.classList;
      if (cl.contains('js-drawer-open') || cl.contains('js-drawer-open-cart') || cl.contains('mw-popup-open')) return true;
      return !!document.querySelector('.pop-up-container[style*="flex"],.pswp--open');
    }

    function cleanupScrollLock() {
      if (document.body.classList.contains('mw-popup-open')) {
        if (!document.querySelector('.pop-up-container[style*="flex"]')) {
          document.body.classList.remove('mw-popup-open');
          document.body.style.overflow = '';
        }
      }
      if (document.body.style.overflow === 'hidden' && !isAnythingOpen()) {
        document.body.style.overflow = '';
      }
    }

    document.addEventListener('touchmove', function(e) {
      if (document.body.style.overflow === 'hidden' && !isAnythingOpen()) {
        e.stopImmediatePropagation();
      }
    }, { capture: true, passive: true });

    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        setTimeout(cleanupScrollLock, 80);
      }
    });
    document.addEventListener('click', function(e) {
      if (document.body.style.overflow === 'hidden') {
        setTimeout(cleanupScrollLock, 80);
      }
    }, { capture: true });

    setInterval(function() {
      if (document.body.style.overflow === 'hidden' || document.body.classList.contains('mw-popup-open')) {
        cleanupScrollLock();
      }
    }, 1000);
  }

  function init() {
    initScrollFix();

    // Milano (2026-06-29): one delegated Contact Us trigger for the whole page.
    // Any [data-mw-open-contact] element — the consult card under the ATC button
    // AND the product-guidance "Contact Us" button — opens the same in-page
    // contact popup (call / live chat / book). Registered once; on <a> triggers
    // the href stays a no-JS fallback.
    if (!window.__mwContactDelegated) {
      window.__mwContactDelegated = true;
      document.addEventListener('click', function(e) {
        var trigger = e.target && e.target.closest ? e.target.closest('[data-mw-open-contact]') : null;
        if (!trigger) return;
        e.preventDefault();
        openContactPopup();
      });
    }

    // If the review card rendered before DN painted its discounted price, re-render it
    // once the price state resolves (fires once) so the base reflects the DN discount,
    // not the MSRP. Idempotent — guarded by wiz.reviewed.
    document.addEventListener('milano:price-ready', function () {
      formatWizardPrices();
      if (wiz && wiz.reviewed) updateReviewCard();
    });

    if (!document.body.classList.contains('milano-grace')) {
      var path = window.location.pathname.toLowerCase();
      if (path.indexOf('/products/grace') !== -1) {
        document.body.classList.add('milano-grace');
        log('[Milano Wizard] Grace detected via URL fallback');
      }
    }

    if (tryTransform()) return;

    var mo = new MutationObserver(function() {
      if (tryTransform()) mo.disconnect();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    setTimeout(function() { try { mo.disconnect(); } catch (e) {} }, 30000);
  }

  function tryTransform() {
    // Milano 2026-06-29: shop-this-look products render the FLAT STL layout
    // (personalize-options.js), NOT the step-by-step wizard. Keep the Grace length
    // photo-grid, but skip the wizard transform so milano-wizard-mode is never set —
    // which also disables per-length banding (applyLengthVisibility bails without
    // milano-wizard-mode), so both lengths show identical fit options. Return true so
    // init() stops here (no retry observer); personalize-options.js owns the panel.
    if (document.querySelector('[data-mw-stl-product]')) {
      document.body.classList.add('milano-wizard-ready');
      if (document.body.classList.contains('milano-grace')) {
        markGraceLengthGrid();
        addGraceHelpLinks();
      }
      return true;
    }

    var panel = document.querySelector('.milano-panel');
    var content = panel ? panel.querySelector('.milano-panel__content') : null;
    var rows = content ? content.querySelectorAll('.milano-option-row') : [];
    if (rows.length === 0) return false;
    try {
      transform(panel, content, rows);
      requestAnimationFrame(function() {
        document.body.classList.add('milano-wizard-ready');
      });
    }
    catch (e) {
      console.error('[Milano Wizard] Transform failed:', e);
      cleanup();
      document.body.classList.add('milano-wizard-ready');
    }
    return true;
  }

  // =========================================================================
  // TRANSFORM
  // =========================================================================

  function transform(panel, content, rows) {
    var stepMap = {}, i, s;
    for (i = 0; i < rows.length; i++) {
      var optName = rows[i].getAttribute('data-option-name') || '';
      var stepId = classifyRow(optName);
      if (!stepMap[stepId]) {
        var def = STEP_DEFS[stepId] || { id: stepId, label: 'Options', hint: '', subhint: '' };
        stepMap[stepId] = { id: stepId, label: def.label, hint: def.hint, subhint: def.subhint, helpId: def.helpId || null, helpText: def.helpText || null, rows: [], done: false, viewed: false };
      }
      stepMap[stepId].rows.push(rows[i]);
    }
    var stepIds = Object.keys(stepMap).map(Number).sort();
    for (s = 0; s < stepIds.length; s++) wiz.steps.push(stepMap[stepIds[s]]);
    if (wiz.steps.length === 0) return;
    updateSecureStepContext();

    for (i = 0; i < rows.length; i++) {
      var rn = (rows[i].getAttribute('data-option-name') || '').toLowerCase();
      if (rn.indexOf('topper') > -1 || rn.indexOf('topgrip') > -1) { wiz.isTopper = true; break; }
    }
    // Fallback: some topper SC-option sets use generic option names ("Cap Size",
    // "Style", "Lace Front") with no "topper" token, which left the wizard
    // calling the product a "Wig". Detect toppers by product title too (a wig
    // title never contains "topper").
    if (!wiz.isTopper) {
      var mwTitleEl = document.querySelector('.product__title');
      var mwTitle = mwTitleEl ? (mwTitleEl.textContent || '').toLowerCase() : '';
      if (mwTitle.indexOf('topper') > -1 || mwTitle.indexOf('top piece') > -1) { wiz.isTopper = true; }
    }

    (function() {
      var path = (window.location.pathname || '').toLowerCase();
      if (path.indexOf('/collections/accessories') > -1) { wiz.isAccessory = true; return; }
      var bodyClass = (document.body.className || '').toLowerCase();
      if (bodyClass.indexOf('accessor') > -1) { wiz.isAccessory = true; return; }
      var h1 = document.querySelector('.product__title');
      if (h1) {
        var title = (h1.textContent || '').trim();
        if (/^(wig\s+cap|wig\s+grip|wigrip|topgrip|hair\s+spray|spray|brush|comb|stand|silk\s+wrap|scarf|hair\s+net|adhesive|conditioner|shampoo|tape)\b/i.test(title)) {
          wiz.isAccessory = true;
        }
      }
    })();

    renameToggleTitle();

    if (wiz.steps.length < 2) {
      log('[Milano Wizard] ' + wiz.steps.length + ' step — flat options mode');
      flattenOptions(content, rows);
      return;
    }

    document.body.classList.add('milano-wizard-mode');

    // v2026-06-05: removed the inline mw-mobile-styles block — it only carried
    // the .mw-review-card__reminder rule, and that element is no longer emitted
    // (per-step reassurance moved inline to .mw-step-notice). The teardown's
    // getElementById('mw-mobile-styles') guard simply no-ops now.

    var indicatorRow = document.createElement('div');
    indicatorRow.className = 'mw-indicator-row';

    var indicator = document.createElement('div');
    indicator.className = 'mw-indicator';
    // A11y (2026-08-05): named navigation landmark so screen reader users can
    // jump straight to the step rail and move between wizard sections.
    indicator.setAttribute('role', 'navigation');
    indicator.setAttribute('aria-label', 'Personalization steps');
    indicator.addEventListener('click', function(e) {
      var pill = e.target;
      while (pill && !pill.classList.contains('mw-pill')) pill = pill.parentElement;
      if (!pill) return;
      e.preventDefault();
      goToStep(parseInt(pill.getAttribute('data-step'), 10));
    });
    wiz.indicatorEl = indicator;

    var wizTotal = document.createElement('div');
    wizTotal.className = 'mw-wiz-total';
    wizTotal.style.display = 'none';
    wiz.wizTotalEl = wizTotal;

    indicatorRow.appendChild(indicator);
    indicatorRow.appendChild(wizTotal);
    content.insertBefore(indicatorRow, content.firstChild);

    var hintWrap = document.createElement('div');
    hintWrap.className = 'mw-hint-wrap';
    var hint = document.createElement('div');
    hint.className = 'mw-hint';
    // v1.6.42: announce step-change copy to screen readers.
    hint.setAttribute('aria-live', 'polite');
    hint.setAttribute('aria-atomic', 'true');
    var subhint = document.createElement('div');
    subhint.className = 'mw-subhint';
    var stepHelp = document.createElement('button');
    stepHelp.type = 'button';
    stepHelp.className = 'mw-step-help';
    stepHelp.style.display = 'none';
    stepHelp.addEventListener('click', function(e) {
      e.preventDefault();
      var popupId = stepHelp.getAttribute('data-popup-id');
      if (popupId && window.showSecurePopup) {
        window.showSecurePopup(popupId);
      } else if (popupId) {
        var container = document.getElementById(popupId);
        if (container) {
          container.style.display = 'flex';
          document.body.classList.add('mw-popup-open');
        }
      }
    });
    var hintRow = document.createElement('div');
    hintRow.className = 'mw-hint-row';
    hintRow.appendChild(hint);
    hintRow.appendChild(stepHelp);
    hintWrap.appendChild(hintRow);
    hintWrap.appendChild(subhint);
    wiz.hintEl = hint;
    wiz.subhintEl = subhint;
    wiz.stepHelpEl = stepHelp;
    indicatorRow.insertAdjacentElement('afterend', hintWrap);

    // v2026-06-05: .mw-step-notice element RESTORED (inline-only). Appended to
    // `content` directly before the nav so it reads in-context — below the
    // step's options, above Continue. Visibility + copy are driven by
    // updateStepNotice() (shows only when a paid Color/Cut option is selected).
    var stepNotice = document.createElement('div');
    stepNotice.className = 'mw-step-notice';
    stepNotice.style.display = 'none';
    stepNotice.setAttribute('aria-live', 'polite');
    wiz.stepNoticeEl = stepNotice;
    content.appendChild(stepNotice);

    var nav = document.createElement('div');
    nav.className = 'mw-nav';
    nav.innerHTML =
      '<button type="button" class="mw-nav__back">Back</button>' +
      '<button type="button" class="mw-nav__next mw-nav__next--continue">Continue \u2192</button>' +
      '<button type="button" class="mw-nav__skip">Skip to summary \u2193</button>';
    wiz.navEl = nav;
    content.appendChild(nav);

    nav.querySelector('.mw-nav__back').addEventListener('click', function(e) {
      e.preventDefault(); goToStep(wiz.currentStep - 1);
    });
    nav.querySelector('.mw-nav__next').addEventListener('click', function(e) {
      e.preventDefault();
      if (wiz.currentStep < lastVisibleStepIndex()) {
        goToStep(wiz.currentStep + 1);
      } else {
        wiz.reviewed = true;
        updateReviewCard();
        if (wiz.reviewCardEl) {
          wiz.reviewCardEl.style.display = '';
          wiz.reviewCardEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });

    nav.querySelector('.mw-nav__skip').addEventListener('click', function(e) {
      e.preventDefault();
      wiz.reviewed = true;
      updateReviewCard();
      if (wiz.reviewCardEl) {
        wiz.reviewCardEl.style.display = '';
        wiz.reviewCardEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    addToggleTotal();
    buildSegmentedControl();
    buildPersonalizeDesc();
    reorderCapAndLace();
    reorderForGrace();
    bootstrapGraceHelpLinks();
    autoSelectCapSize();
    requestPDPPriceSync('wizard');
    if (window.MILANO_addBootstrap) {
      window.MILANO_addBootstrap(autoSelectCapSize, true);
      window.MILANO_addBootstrap(reorderForGrace, true);
      // v1.6.47: re-assert length-band visibility + dynamic-row step filing
      // through the early-load window (SC re-renders / restored state). Runs
      // all ticks; idempotent no-op when settled.
      window.MILANO_addBootstrap(function() {
        reclassifyDynamicRows();
        applyLengthVisibility();
        return false;
      }, false);
    }

    if (!window._mwReorderResizeHooked) {
      window._mwReorderResizeHooked = true;
      var _resizeTimer = null;
      window.addEventListener('resize', function() {
        if (_resizeTimer) clearTimeout(_resizeTimer);
        _resizeTimer = setTimeout(function() {
          if (document.body.classList.contains('milano-grace')) scheduleGraceReorder();
        }, 120);
      });
    }
    injectConsultCTA();
    enhanceFormElements();
    formatWizardPrices();
    watchToggleTitle();
    injectPriceContext();
    buildReviewCard();
    watchVariantChanges();
    autoBypassColorGate();
    enhanceProductPrice();

    updateToggleTotal();
    updatePriceContext();
    updateWizardTotal();
    fixSkinToSilk();

    setupColorExclusivity();

    var _changeDebounce = null;
    content.addEventListener('change', function() {
      clearTimeout(_changeDebounce);
      _changeDebounce = setTimeout(function() {
        reclassifyDynamicRows(); // re-file SC-revealed sub-groups (Cut inline)
        applyLengthVisibility();
        // v2026-06-25: always re-sync the current step's row display so an SC
        // conditional (Position of Face Framing / Bangs or Fringe) re-revealing
        // on a row ALREADY filed into this step shows again. reclassify only
        // re-renders on a MOVE, so 2nd+ reveals of an already-filed row went stale
        // (Face Framing's dropdown stopped re-appearing after the first toggle).
        refreshCurrentStepRows();
        updateCompletion();
        updateToggleTotal();
        updatePriceContext();
        updateWizardTotal();
        if (wiz.reviewed) updateReviewCard();

        applyLabelRenames();
        injectBadgesAndLabels();
        setupColorExclusivity(); // idempotent — wires groups classed just above
        injectInlineHelp();
        injectAddonHeaders();
        formatWizardPrices();
        syncVirtualCutSkip();
        syncVirtualColorSkip();
        updateStepNotice();

        var nextBtn = wiz.navEl ? wiz.navEl.querySelector('.mw-nav__next') : null;
        if (nextBtn) {
          nextBtn.classList.remove('mw-pulse');
          void nextBtn.offsetWidth;
          nextBtn.classList.add('mw-pulse');
        }
      }, 200);
    });

    content.addEventListener('click', function(e) {
      var optVal = e.target.closest('.bold_option_value');
      if (!optVal) return;
      if (e.target.closest('label, input, a, button, .mw-inline-help')) return;
      var input = optVal.querySelector('input[type="radio"], input[type="checkbox"]');
      if (input) input.click();
    });

    if (!window._mwAlwaysVisibleTotalsHooked) {
      window._mwAlwaysVisibleTotalsHooked = true;
      var alwaysVisibleDebounce = null;
      document.addEventListener('change', function(e) {
        if (!e.target || !e.target.closest || !e.target.closest('.bold_option.milano-always-visible')) return;
        clearTimeout(alwaysVisibleDebounce);
        alwaysVisibleDebounce = setTimeout(function() {
          updateToggleTotal();
          updatePriceContext();
          updateWizardTotal();
          if (wiz.reviewed) updateReviewCard();
          requestPDPPriceSync('wizard');
        }, 60);
      }, true);
    }

    // v1.6.47: first length-band pass BEFORE the initial goToStep so the
    // wrong band's groups never paint (no flash of both Color tiers).
    applyLengthVisibility();

    wiz.active = true;
    goToStep(0);

    document.addEventListener('click', function(e) {
      if (!e.target) return;
      if (e.target.closest('.mw-lace-help, .mw-step-help, [data-popup]')) {
        document.body.dataset.scrollY = String(window.scrollY);
      }
      if (e.target.closest('.close-button') || e.target.classList.contains('close-button-img')) {
        var popup = e.target.closest('.pop-up-container');
        if (popup) {
          popup.style.display = 'none';
          document.body.classList.remove('mw-popup-open');
          document.body.style.overflow = '';
        }
      }
      if (e.target.classList && e.target.classList.contains('pop-up-container')) {
        e.target.style.display = 'none';
        document.body.classList.remove('mw-popup-open');
        document.body.style.overflow = '';
      }
    });

    log('[Milano Wizard] Transform complete \u2014 ' + wiz.steps.length + ' steps');
  }

  // =========================================================================
  // CLASSIFY
  // =========================================================================

  function classifyRow(name) {
    for (var s = 0; s < STEP_DEFS.length; s++)
      for (var p = 0; p < STEP_DEFS[s].patterns.length; p++)
        if (STEP_DEFS[s].patterns[p].test(name)) return STEP_DEFS[s].id;
    return STEP_DEFS[STEP_DEFS.length - 1].id;
  }

  // A few SC option sets intentionally offer Ear Pieces without a separate
  // Secure Method. In that case, keep the same step and controls but make its
  // navigation/copy describe the option that actually exists. Any other row in
  // Secure restores the standard wording, so mixed and future option sets fail
  // safely without product-handle or option-set exceptions.
  function updateSecureStepContext() {
    var secureStep = null;
    for (var s = 0; s < wiz.steps.length; s++) {
      if (wiz.steps[s].id === 0) { secureStep = wiz.steps[s]; break; }
    }
    if (!secureStep) return;

    var hasEarPieces = false;
    var hasOtherSecureOption = false;
    for (var r = 0; r < secureStep.rows.length; r++) {
      var name = (secureStep.rows[r].getAttribute('data-option-name') || '').trim();
      if (/ear pieces?/i.test(name)) hasEarPieces = true;
      else if (name) hasOtherSecureOption = true;
    }

    var earPiecesCopy = null;
    for (var a = 0; a < ADDON_HEADERS.length; a++) {
      if (ADDON_HEADERS[a].nameMatch === 'ear piece') { earPiecesCopy = ADDON_HEADERS[a]; break; }
    }

    secureStep.earPiecesOnly = hasEarPieces && !hasOtherSecureOption && !!earPiecesCopy;
    if (secureStep.earPiecesOnly) {
      secureStep.label = 'Ear Pieces';
      secureStep.hint = earPiecesCopy.title;
      secureStep.subhint = earPiecesCopy.desc;
      secureStep.helpId = earPiecesCopy.helpId;
      secureStep.helpText = earPiecesCopy.helpText;
    } else {
      secureStep.label = STEP_DEFS[0].label;
      secureStep.hint = STEP_DEFS[0].hint;
      secureStep.subhint = STEP_DEFS[0].subhint;
      secureStep.helpId = STEP_DEFS[0].helpId;
      secureStep.helpText = STEP_DEFS[0].helpText;
    }
  }

  function isEarPiecesOnlySecureRow(row) {
    for (var s = 0; s < wiz.steps.length; s++) {
      var step = wiz.steps[s];
      if (step.id === 0 && step.earPiecesOnly && step.rows.indexOf(row) > -1) return true;
    }
    return false;
  }

  // v1.6.47: SC's conditional sub-groups (Position of Face Framing, Bangs or
  // Fringe) render at load as EMPTY unnamed shells ("Option 4"/"Option 5"),
  // so transform() can only file them into the default last step (Style).
  // When the shopper picks the trigger Cut value, SC reveals the group and
  // options.js renames the row to its real name — this re-files such rows
  // into the step their real name classifies to, so the dropdown appears
  // INLINE on the Cut screen (it already sits right under the Cut group in
  // the DOM; step membership is array-based, no DOM move needed).
  function reclassifyDynamicRows() {
    if (!wiz.active || wiz.steps.length < 2) return false;
    var moved = false;
    for (var s = 0; s < wiz.steps.length; s++) {
      var rows = wiz.steps[s].rows;
      for (var r = rows.length - 1; r >= 0; r--) {
        var name = rows[r].getAttribute('data-option-name') || '';
        if (!name || /^option\s*\d+$/i.test(name)) continue; // still a shell
        var targetId = classifyRow(name);
        if (targetId === wiz.steps[s].id) continue;
        var target = null;
        for (var t = 0; t < wiz.steps.length; t++) {
          if (wiz.steps[t].id === targetId) { target = wiz.steps[t]; break; }
        }
        if (!target) continue; // no such step on this product — leave as-is
        target.rows.push(rows.splice(r, 1)[0]);
        moved = true;
      }
    }
    if (moved) {
      updateSecureStepContext();
      // Re-derive row display + separators + pills for the new membership.
      // quiet: a reveal must never scroll the page.
      goToStep(wiz.currentStep, true);
    }
    return moved;
  }

  // =========================================================================
  // GRACE PER-LENGTH OPTION VISIBILITY (v1.6.47)
  // =========================================================================

  function getCurrentLengthBand() {
    var checked = null;
    var fieldsets = document.querySelectorAll('fieldset.radio__fieldset--variant-option-image');
    if (fieldsets.length === 1) {
      // Steady state (no re-render in flight): skip the computed-style
      // visibility walk entirely — this runs from the change debounce on
      // every wizard click.
      checked = fieldsets[0].querySelector('input[type="radio"]:checked');
    } else if (fieldsets.length > 1) {
      // Palo Alto double-buffers variant selectors during viewTransition; a
      // hidden stale fieldset can briefly carry an outdated :checked radio.
      // Prefer the VISIBLE fieldset (same approach as reorderForGrace).
      for (var i = 0; i < fieldsets.length && !checked; i++) {
        if (isNodeDisplayHidden(fieldsets[i])) continue;
        checked = fieldsets[i].querySelector('input[type="radio"]:checked');
      }
      for (var j = 0; j < fieldsets.length && !checked; j++) {
        checked = fieldsets[j].querySelector('input[type="radio"]:checked');
      }
    }
    if (!checked) {
      checked = document.querySelector('input[data-single-option-selector][name="options[Length]"]:checked');
    }
    if (!checked) return null;

    // Radio value is the raw option value ("19\" Nicole") — independent of
    // the .mw-len-num/.mw-len-name label spans options.js injects.
    var raw = (checked.value || '');
    if (!raw) {
      var btn = checked.closest('.radio__button');
      var titleEl = btn ? btn.querySelector('.option-title') : null;
      raw = titleEl ? (titleEl.textContent || '') : '';
    }
    raw = raw.toLowerCase();
    if (!raw) return null;

    var inches = parseInt(raw, 10);
    if (inches && LENGTH_BAND_BY_INCHES[inches]) return LENGTH_BAND_BY_INCHES[inches];

    for (var name in LENGTH_BAND_BY_NAME) {
      if (LENGTH_BAND_BY_NAME.hasOwnProperty(name) && raw.indexOf(name) > -1) {
        return LENGTH_BAND_BY_NAME[name];
      }
    }
    return null;
  }

  function stepHasVisibleRows(step) {
    if (!step) return false;
    for (var r = 0; r < step.rows.length; r++) {
      if (!step.rows[r].classList.contains('milano-option-row--hidden')) return true;
    }
    return false;
  }

  function lastVisibleStepIndex() {
    for (var i = wiz.steps.length - 1; i >= 0; i--) {
      if (stepHasVisibleRows(wiz.steps[i])) return i;
    }
    return wiz.steps.length - 1;
  }

  // Re-sync the CURRENT step's row display + group separators after a
  // visibility change, without goToStep's scrollIntoView side effect (a
  // Length click far above the wizard must not yank the viewport to it).
  function refreshCurrentStepRows() {
    if (!wiz.active || !wiz.steps[wiz.currentStep]) return;
    var rows = wiz.steps[wiz.currentStep].rows;
    var firstVisible = true;
    for (var g = 0; g < rows.length; g++) {
      rows[g].classList.remove('mw-step-group-sep');
      rows[g].style.display = rows[g].classList.contains('milano-option-row--hidden') ? 'none' : '';
      if (rows[g].style.display === 'none') continue;
      if (!firstVisible) rows[g].classList.add('mw-step-group-sep');
      firstVisible = false;
    }
  }

  // SC charges whatever is CHECKED, visible or not — a length-hidden row
  // must therefore carry no selections. Native change dispatches inline;
  // jQuery triggers are queued by the caller and deferred 150ms (the
  // clearAllWizardSelections pattern) so SC's jQuery handlers can't
  // re-render the panel while we're still iterating rows.
  function clearLengthHiddenRowSelections(row, jqQueue) {
    var cleared = 0;
    var inputs = row.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
    for (var i = 0; i < inputs.length; i++) {
      inputs[i].checked = false;
      inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
      if (jqQueue) jqQueue.push(inputs[i]);
      cleared++;
    }
    var selects = row.querySelectorAll('select');
    for (var s = 0; s < selects.length; s++) {
      if (selects[s].selectedIndex > 0) {
        selects[s].selectedIndex = 0;
        selects[s].dispatchEvent(new Event('change', { bubbles: true }));
        if (jqQueue) jqQueue.push(selects[s]);
        cleared++;
      }
    }
    return cleared > 0;
  }

  // Scoped replacement for running global autoSelectDefaults on band
  // changes: re-check the "keep as pictured" value of ONE just-reshown row,
  // only if it has no selection at all. The global pass is deliberately NOT
  // used here — it would re-impose defaults on unrelated groups the shopper
  // emptied on purpose.
  function applySkipDefaultToRow(row) {
    var body = row.querySelector('.milano-option-row__body');
    if (!body) return;
    if (body.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) return;
    var selects = body.querySelectorAll('select');
    for (var s = 0; s < selects.length; s++) {
      if (selects[s].selectedIndex > 0) return;
    }
    var vals = body.querySelectorAll('.bold_option_value');
    for (var v = 0; v < vals.length; v++) {
      var lbl = vals[v].querySelector('label');
      if (!lbl) continue;
      if (!isSkipDefaultLabel(stripPriceText(lbl.textContent))) continue;
      var input = vals[v].querySelector('input[type="radio"], input[type="checkbox"]');
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }
  }

  // If a virtual "Keep as pictured" card's host row got length-hidden, move
  // the card (+ its separator) into the step's first visible row so the skip
  // choice stays reachable. Node move preserves the card's listeners.
  function ensureVirtualCardHosts() {
    var cfgs = [VIRTUAL_SKIP_CONFIGS.cut, VIRTUAL_SKIP_CONFIGS.color];
    for (var c = 0; c < cfgs.length; c++) {
      var cfg = cfgs[c];
      var card = document.querySelector('.' + cfg.virtualClass);
      if (!card) continue;
      var hostRow = card.closest('.milano-option-row');
      if (!hostRow || !hostRow.classList.contains('milano-option-row--hidden')) continue;

      var step = null;
      for (var i = 0; i < wiz.steps.length; i++) {
        if (wiz.steps[i].id === cfg.stepId) { step = wiz.steps[i]; break; }
      }
      if (!step) continue;

      var target = null;
      for (var r = 0; r < step.rows.length; r++) {
        if (step.rows[r].classList.contains('milano-option-row--hidden')) continue;
        if (cfg.skipSubgates && step.rows[r].classList.contains('mw-subgate')) continue;
        target = step.rows[r];
        break;
      }
      if (!target) continue;
      var body = target.querySelector('.milano-option-row__body');
      if (!body || body.querySelector('.' + cfg.virtualClass)) continue;

      var sep = (card.nextElementSibling && card.nextElementSibling.classList.contains(cfg.sepClass))
        ? card.nextElementSibling : null;
      body.insertBefore(card, body.firstChild);
      if (sep) body.insertBefore(sep, card.nextSibling);
    }
  }

  var _lengthBandWarned = false;

  function applyLengthVisibility() {
    if (!document.body || !document.body.classList.contains('milano-grace')) return false;
    if (!document.body.classList.contains('milano-wizard-mode')) return false;
    if (!wiz.steps.length) return false;

    var band = getCurrentLengthBand();
    if (band === null && !_lengthBandWarned) {
      _lengthBandWarned = true;
      console.warn('[Milano Wizard] Length band unresolved — banded option groups hidden (fail-safe)');
    }

    var changed = false;
    var reshown = [];
    var jqQueue = [];

    // Pass 1 — collect every named row. Names are re-read each pass (no
    // caching: options.js renames "Option N" shells once SC fills them), and
    // the full normalized name list lets the "- Processed" twin convention
    // pair a short-band group with its long-band sibling.
    var entries = [];
    var normalizedNames = [];
    for (var s = 0; s < wiz.steps.length; s++) {
      for (var r = 0; r < wiz.steps[s].rows.length; r++) {
        var row = wiz.steps[s].rows[r];
        var name = row.getAttribute('data-option-name') || '';
        if (!name) {
          var titleEl = row.querySelector('.bold_option_title');
          name = titleEl ? (titleEl.textContent || '').trim() : '';
        }
        if (!name) continue; // unnamed (SC conditional shells) — never touch
        entries.push({ row: row, name: name });
        normalizedNames.push(mwNormalizeOptionName(name));
      }
    }

    // Pass 2 — classify + apply.
    for (var e = 0; e < entries.length; e++) {
      var entryRow = entries[e].row;
      var decision = lengthBandDecision(entries[e].name, normalizedNames, band);
      if (decision === null) continue; // not banded — fail open

      if (decision === true) {
        // Wrong band (or unresolved length → fail safe): hide + clear.
        if (!entryRow.classList.contains('milano-option-row--hidden')) {
          entryRow.classList.add('milano-option-row--hidden');
          changed = true;
        }
        if (!entryRow.hasAttribute('data-mw-length-hidden')) {
          entryRow.setAttribute('data-mw-length-hidden', '1');
          changed = true;
        }
        if (clearLengthHiddenRowSelections(entryRow, jqQueue)) changed = true;
      } else if (entryRow.hasAttribute('data-mw-length-hidden')) {
        // Matching band again: release OUR hide, but never un-hide a row
        // SC's own conditions are hiding (options.js owns that state).
        entryRow.removeAttribute('data-mw-length-hidden');
        var bold = entryRow.querySelector('.bold_option');
        if (!bold || bold.style.display !== 'none') {
          entryRow.classList.remove('milano-option-row--hidden');
          reshown.push(entryRow);
        }
        changed = true;
      }
    }

    if (!changed) return false;
    log('[Milano Wizard] Length band applied: ' + (band || 'unresolved'));

    // Deferred jQuery notification for cleared inputs (house pattern from
    // clearAllWizardSelections — lets SC settle after our native dispatches).
    if (jqQueue.length && window.jQuery) {
      setTimeout(function() {
        try {
          for (var q = 0; q < jqQueue.length; q++) window.jQuery(jqQueue[q]).trigger('change');
        } catch (e) { /* ignore */ }
      }, 150);
    }

    updateCompletion(); // recomputes done flags + re-renders pills
    refreshCurrentStepRows();
    ensureVirtualCardHosts();
    // Just-reshown groups regain their "keep as pictured" default — scoped
    // to those rows only (never re-imposes defaults elsewhere).
    if (wiz.personalizeDefaultsApplied) {
      for (var rs = 0; rs < reshown.length; rs++) applySkipDefaultToRow(reshown[rs]);
    }
    if (wiz.active && wiz.steps[wiz.currentStep] && !stepHasVisibleRows(wiz.steps[wiz.currentStep])) {
      goToStep(wiz.currentStep, true); // step emptied under the shopper — advance, no scroll
    }
    updateToggleTotal();
    updatePriceContext();
    updateWizardTotal();
    if (wiz.reviewed) updateReviewCard();
    updateStepNotice();
    requestPDPPriceSync('length-band');
    return true;
  }

  // =========================================================================
  // GO TO STEP
  // =========================================================================

  function goToStep(stepIndex, quiet) {
    if (stepIndex < 0) stepIndex = 0;
    if (stepIndex >= wiz.steps.length) stepIndex = wiz.steps.length - 1;

    // v1.6.47: steps whose rows are ALL hidden (length-banded groups) are
    // skipped in the direction of travel; if that runs off the end, search
    // the other way. Guard-capped; with every step empty (impossible in
    // practice — Secure is never banded) the original index stands.
    if (wiz.steps[stepIndex] && !stepHasVisibleRows(wiz.steps[stepIndex])) {
      var dir = stepIndex >= wiz.currentStep ? 1 : -1;
      var probe = stepIndex, guard = 0;
      while (guard++ <= wiz.steps.length) {
        probe += dir;
        if (probe < 0 || probe >= wiz.steps.length) { dir = -dir; probe = stepIndex; continue; }
        if (stepHasVisibleRows(wiz.steps[probe])) { stepIndex = probe; break; }
      }
    }

    wiz.currentStep = stepIndex;

    if (wiz.steps[stepIndex]) wiz.steps[stepIndex].viewed = true;

    for (var i = 0; i < wiz.steps.length; i++) {
      var isActive = i === stepIndex;
      for (var r = 0; r < wiz.steps[i].rows.length; r++) {
        var row = wiz.steps[i].rows[r];
        row.style.display = (!isActive || row.classList.contains('milano-option-row--hidden')) ? 'none' : '';
        row.classList.remove('mw-step-group-sep');
      }
    }

    var firstVisible = true;
    for (var g = 0; g < wiz.steps[stepIndex].rows.length; g++) {
      var gRow = wiz.steps[stepIndex].rows[g];
      if (gRow.style.display === 'none' || gRow.classList.contains('milano-option-row--hidden')) continue;
      if (!firstVisible) gRow.classList.add('mw-step-group-sep');
      firstVisible = false;
    }

    renderIndicator();

    if (wiz.hintEl) {
      var hintText = wiz.steps[stepIndex].hint || '';
      if (wiz.isTopper) hintText = hintText.replace(/your wig/gi, 'your topper').replace(/style it/gi, 'style it');
      wiz.hintEl.textContent = hintText;
    }
    if (wiz.subhintEl) {
      var sh = wiz.steps[stepIndex].subhint || '';
      if (wiz.isTopper) sh = sh.replace(/your wig/gi, 'your topper').replace(/each wig/gi, 'each topper');
      wiz.subhintEl.textContent = sh;
      wiz.subhintEl.style.display = sh ? '' : 'none';
    }

    if (wiz.stepHelpEl) {
      var stepDef = wiz.steps[stepIndex];
      var helpId = resolveStepHelpId(stepDef);
      var helpText = stepDef.helpText || 'Learn more';

      if (helpId) {
        wiz.stepHelpEl.setAttribute('data-popup-id', helpId);
        wiz.stepHelpEl.innerHTML =
          '<svg class="mw-step-help__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
            '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
            '<text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="10" font-weight="700" font-family="inherit">?</text>' +
          '</svg>' +
          '<span>' + helpText + '</span>';
        wiz.stepHelpEl.style.display = '';
      } else {
        wiz.stepHelpEl.style.display = 'none';
      }
    }

    var backBtn = wiz.navEl.querySelector('.mw-nav__back');
    var nextBtn = wiz.navEl.querySelector('.mw-nav__next');
    var skipBtn = wiz.navEl.querySelector('.mw-nav__skip');
    backBtn.style.display = stepIndex > 0 ? '' : 'none';

    if (stepIndex >= lastVisibleStepIndex()) {
      nextBtn.textContent = '\u2713 Review My ' + (wiz.isTopper ? 'Topper' : 'Wig');
      nextBtn.className = 'mw-nav__next mw-nav__next--review';
      if (skipBtn) skipBtn.style.display = 'none';
    } else {
      nextBtn.textContent = 'Continue \u2192';
      nextBtn.className = 'mw-nav__next mw-nav__next--continue';
      if (skipBtn) skipBtn.style.display = '';
    }

    updateCompletion();
    applyLabelRenames();
    syncVirtualCutSkip();
    syncVirtualColorSkip();

    var animTargets = [wiz.hintEl, wiz.subhintEl];
    if (wiz.stepHelpEl && wiz.stepHelpEl.style.display !== 'none') {
      animTargets.push(wiz.stepHelpEl);
    }
    for (var ri = 0; ri < wiz.steps[stepIndex].rows.length; ri++) {
      var rRow = wiz.steps[stepIndex].rows[ri];
      if (rRow.style.display === 'none') continue;
      if (rRow.classList.contains('milano-option-row--hidden')) continue;
      animTargets.push(rRow);
    }
    // v1.6.47 perf: restart the enter animation with ONE forced reflow for
    // the whole batch (remove all → single layout flush → add all) instead
    // of a forced layout per element — goToStep runs on every step change.
    for (var t = 0; t < animTargets.length; t++) {
      if (animTargets[t]) animTargets[t].classList.remove('mw-anim-enter');
    }
    // eslint-disable-next-line no-unused-expressions
    void document.body.offsetWidth;
    for (var t2 = 0; t2 < animTargets.length; t2++) {
      if (animTargets[t2]) animTargets[t2].classList.add('mw-anim-enter');
    }

    if (!quiet) {
      var panelEl = document.querySelector('.milano-panel');
      if (panelEl) panelEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    updateStepNotice();

    // A11y (2026-08-05): tell milano-a11y.js which step is now showing so it
    // can announce "Step N of M: {label}" through the polite live region.
    // Fail-silent; `quiet` transitions (programmatic re-syncs) stay silent.
    if (!quiet) {
      try {
        var visibleCount = 0, visibleIndex = 0;
        for (var vs = 0; vs < wiz.steps.length; vs++) {
          if (stepHasVisibleRows(wiz.steps[vs])) {
            visibleCount++;
            if (vs === stepIndex) visibleIndex = visibleCount;
          }
        }
        document.dispatchEvent(new CustomEvent('milano:wizard:step', {
          detail: {
            index: visibleIndex,
            total: visibleCount,
            label: (wiz.steps[stepIndex] && wiz.steps[stepIndex].label) || ''
          }
        }));
      } catch (e) { /* fail silently */ }
    }
  }

  // =========================================================================
  // STEP REASSURANCE NOTICE (v2026-06-05, inline-only)
  // Shows the .mw-step-notice card on the Color/Cut step ONLY when a PAID
  // option is selected there (skip/keep-as-pictured choices don't trigger it).
  // =========================================================================

  function updateStepNotice() {
    if (!wiz.stepNoticeEl) return;

    var step = wiz.steps[wiz.currentStep];
    var stepId = step ? step.id : -1;
    var config = STEP_REASSURANCE[stepId] || null;
    if (!step || !config) {
      wiz.stepNoticeEl.style.display = 'none';
      wiz.stepNoticeEl.removeAttribute('data-mw-notice-state');
      return;
    }

    var hasPaidSelection = false;
    for (var r = 0; r < step.rows.length && !hasPaidSelection; r++) {
      var row = step.rows[r];
      if (row.classList.contains('milano-option-row--hidden')) continue;
      var checked = row.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
      for (var c = 0; c < checked.length; c++) {
        var val = checked[c].closest('.bold_option_value');
        if (!val) continue;
        // Skip the "keep as pictured" / no-cut / no-color / no-style choices.
        if (val.classList.contains('mw-no-color') ||
            val.classList.contains('mw-no-cut') ||
            val.classList.contains('mw-no-style')) continue;
        var priceEl = val.querySelector('.bold_option_value_price');
        var price = priceEl ? (parseFloat((priceEl.textContent || '').replace(/[^\d.]/g, '')) || 0) : 0;
        if (price > 0) { hasPaidSelection = true; break; }
      }
    }

    // Skip redundant DOM work (and avoid re-firing the fade) when neither the
    // step nor the paid-selection state changed since the last call.
    var noticeState = stepId + ':' + (hasPaidSelection ? '1' : '0');
    if (wiz.stepNoticeEl.getAttribute('data-mw-notice-state') === noticeState) return;
    wiz.stepNoticeEl.setAttribute('data-mw-notice-state', noticeState);

    if (!hasPaidSelection) {
      wiz.stepNoticeEl.style.display = 'none';
      return;
    }

    wiz.stepNoticeEl.innerHTML =
      '<svg class="mw-step-notice__icon" width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
        '<rect x="2" y="4.5" width="16" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/>' +
        '<path d="M2.5 6L10 11L17.5 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
      '</svg>' +
      '<span class="mw-step-notice__text"><strong>' + config.lead + '</strong> ' + config.body + '</span>';
    wiz.stepNoticeEl.style.display = '';
  }

  // =========================================================================
  // INDICATOR
  // =========================================================================

  function renderIndicator() {
    syncPersonalizationEntryLabel();
    if (!wiz.indicatorEl) return;
    var html = '';
    var emitted = 0;
    for (var i = 0; i < wiz.steps.length; i++) {
      var step = wiz.steps[i];
      // v1.6.47: steps with zero visible rows (length-banded groups hidden
      // for the selected Length) drop out of the pill nav entirely.
      if (!stepHasVisibleRows(step)) continue;
      var isActive = i === wiz.currentStep;
      var isDone = step.done && step.viewed && !isActive;
      var cls = 'mw-pill';
      if (isActive) cls += ' mw-pill--active';
      if (isDone) cls += ' mw-pill--done';

      if (emitted > 0) html += '<span class="mw-pill__sep"></span>';
      // v1.6.42: aria-current="step" on active pill — gives SR users
      // sequence context within the wizard's step-pill nav.
      html += '<button type="button" class="' + cls + '" data-step="' + i + '"' +
              (isActive ? ' aria-current="step"' : '') + '>';
      if (isDone) {
        html += '<svg width="10" height="10" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round"/></svg>';
      }
      html += ' <span class="mw-pill__label">' + step.label + '</span></button>';
      emitted++;
    }
    // v1.6.47 perf: this runs from every change debounce via
    // updateCompletion. Identical output → skip the innerHTML rebuild
    // (rebuilding replaces the pill buttons — DOM churn + it would drop
    // keyboard focus from a focused pill for no reason).
    if (html === wiz._lastIndicatorHtml) return;
    wiz._lastIndicatorHtml = html;
    wiz.indicatorEl.innerHTML = html;
  }

  // =========================================================================
  // COMPLETION
  // =========================================================================

  function updateCompletion() {
    for (var i = 0; i < wiz.steps.length; i++) {
      var has = false;
      for (var r = 0; r < wiz.steps[i].rows.length; r++) {
        var row = wiz.steps[i].rows[r];
        if (!row.classList.contains('milano-option-row--hidden') && row.classList.contains('milano-option-row--selected')) { has = true; break; }
      }
      if (!has && wiz.steps[i].id === 2 && wiz.virtualCutSelected) {
        has = true;
      }
      if (!has && wiz.steps[i].id === 1 && wiz.virtualColorSelected) {
        has = true;
      }
      wiz.steps[i].done = has;
    }
    renderIndicator();
  }

  // =========================================================================
  // RENAME TOGGLE
  // =========================================================================

  function renameToggleTitle() {
    var titleEl = document.querySelector('.personalize-toggle__title');
    if (!titleEl) return;
    var subtitleEl = document.querySelector('.personalize-toggle__subtitle');
    var prodType = wiz.isTopper ? 'Topper' : 'Wig';
    titleEl.textContent = 'Personalize This ' + prodType;

    if (!subtitleEl) return;

    var parts = [];
    var hasSecure = false, hasEar = false, hasHairline = false;
    var rows = document.querySelectorAll('.milano-option-row');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].classList.contains('milano-option-row--hidden')) continue;
      var name = (rows[i].getAttribute('data-option-name') || '').toLowerCase();
      if (name.indexOf('secure') > -1 && !hasSecure) { parts.push('secure method'); hasSecure = true; }
      if (name.indexOf('ear') > -1 && !hasEar) { parts.push('ear pieces'); hasEar = true; }
      if ((name.indexOf('hairline') > -1 || name.indexOf('blending') > -1) && !hasHairline) { parts.push('hairline'); hasHairline = true; }
    }

    if (parts.length === 0) {
      subtitleEl.textContent = 'Choose your options';
    } else if (parts.length === 1) {
      subtitleEl.textContent = 'Choose your ' + parts[0];
    } else {
      var last = parts.pop();
      subtitleEl.textContent = parts.join(', ') + ' & ' + last;
    }
  }

  // =========================================================================
  // FLAT OPTIONS MODE
  // =========================================================================

  function flattenOptions(content, rows) {
    document.body.classList.add('milano-flat-options');

    if (!document.getElementById('mw-flat-styles')) {
      var style = document.createElement('style');
      style.id = 'mw-flat-styles';
      style.textContent =
        'body.milano-flat-options .milano-option-row { border: none; padding: 0; margin: 0 0 4px; }' +
        'body.milano-flat-options .milano-option-row:first-child { border-top: none; margin-top: 0; }' +
        'body.milano-flat-options .milano-option-row__body { max-height: none !important; overflow: visible !important; }' +
        'body.milano-flat-options .milano-option-row__header { display: none !important; }' +
        'body.milano-flat-options .milano-option-row__body .bold_option { padding-bottom: 6px; }' +
        'body.milano-flat-options .milano-summary { display: none !important; }';
      document.head.appendChild(style);
    }

    for (var i = 0; i < rows.length; i++) {
      var header = rows[i].querySelector('.milano-option-row__header');
      if (header) header.style.display = 'none';

      var body = rows[i].querySelector('.milano-option-row__body');
      if (body) {
        body.style.maxHeight = 'none';
        body.style.overflow = 'visible';
        body.style.display = 'block';
      }

      if (!rows[i].classList.contains('milano-option-row--hidden')) {
        rows[i].style.display = '';
      }
    }

  }

  // =========================================================================
  // TOGGLE — Services total
  // =========================================================================

  function addToggleTotal() {
    var titleEl = document.querySelector('.personalize-toggle__title');
    if (!titleEl) return;
    var totalSpan = document.createElement('span');
    totalSpan.className = 'mw-toggle-total';
    titleEl.parentNode.insertBefore(totalSpan, titleEl.nextSibling);
    wiz.toggleTotalEl = totalSpan;
  }

  // =========================================================================
  // SEGMENTED ENTRY CONTROL
  // =========================================================================

  function getPersonalizationEntryLabel() {
    var labels = [];
    for (var s = 0; s < wiz.steps.length; s++) {
      if (wiz.steps[s].label && stepHasVisibleRows(wiz.steps[s])) labels.push(wiz.steps[s].label);
    }
    return labels.length ? 'Add ' + labels.join(' · ') : 'Personalize';
  }

  function syncPersonalizationEntryLabel() {
    if (!wiz.entryLabelEl) return;
    var label = getPersonalizationEntryLabel();
    if (wiz.entryLabelEl.textContent !== label) wiz.entryLabelEl.textContent = label;
  }

  function buildSegmentedControl() {
    if (wiz.isAccessory) return;

    var toggleEl = document.querySelector('.personalize-toggle');
    if (!toggleEl || toggleEl.querySelector('.mw-segmented')) return;
    var prodType = wiz.isTopper ? 'Topper' : 'Wig';

    var title = document.createElement('div');
    title.className = 'mw-entry-choice-title';
    var titleText = document.createElement('span');
    titleText.className = 'mw-entry-choice-title__text';
    titleText.textContent = 'Personalize Your ' + prodType;
    title.appendChild(titleText);
    // Proactive "Required" signal — visible BEFORE the shopper hits ATC, and
    // distinct from the post-ATC "Please choose one option" error below. CSS
    // hides it once a choice is made (body.mw-entry-choice-made). It's
    // aria-hidden because the radiogroup already carries aria-required="true",
    // so this is purely visual reinforcement (no double announcement).
    var reqPill = document.createElement('span');
    reqPill.className = 'mw-required-pill';
    reqPill.setAttribute('aria-hidden', 'true');
    reqPill.textContent = 'Required';
    title.appendChild(reqPill);

    var seg = document.createElement('div');
    seg.className = 'mw-segmented';
    seg.setAttribute('role', 'radiogroup');
    seg.setAttribute('aria-required', 'true');
    seg.setAttribute('aria-label', 'Personalize Your ' + prodType + ': personalize this ' + prodType.toLowerCase() + ' or ship it as pictured');
    seg.setAttribute('data-mw-entry-choice', '');

    seg.innerHTML =
      '<button type="button" class="mw-segmented__cell mw-segmented__cell--personalize" role="radio" aria-checked="false" data-segment="personalize">' +
        '<span class="mw-segmented__check" aria-hidden="true">' +
          '<svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</span>' +
        '<span class="mw-segmented__content">' +
          '<span class="mw-segmented__answer mw-segmented__answer--yes">Yes</span>' +
          '<span class="mw-segmented__label">' + getPersonalizationEntryLabel() + '</span>' +
        '</span>' +
      '</button>' +
      '<span class="mw-segmented__or" aria-hidden="true">or</span>' +
      '<button type="button" class="mw-segmented__cell mw-segmented__cell--asis" role="radio" aria-checked="false" data-segment="asis">' +
        '<span class="mw-segmented__check" aria-hidden="true">' +
          '<svg width="14" height="14" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</span>' +
        '<span class="mw-segmented__content">' +
          '<span class="mw-segmented__answer mw-segmented__answer--no">No</span>' +
          '<span class="mw-segmented__label">Ship As Pictured</span>' +
          '<span class="mw-segmented__sub">with Clips &amp; Combs</span>' +
        '</span>' +
      '</button>';

    toggleEl.insertBefore(title, toggleEl.firstChild);
    title.insertAdjacentElement('afterend', seg);
    wiz.entryTitleEl = title;
    wiz.segmentedEl = seg;
    wiz.entryLabelEl = seg.querySelector('.mw-segmented__cell--personalize .mw-segmented__label');

    var error = document.createElement('div');
    error.className = 'mw-entry-required';
    error.id = 'mw-entry-required';
    error.setAttribute('role', 'alert');
    error.setAttribute('aria-live', 'polite');
    error.style.display = 'none';
    error.textContent = 'Please choose one option before adding to cart.';
    seg.insertAdjacentElement('afterend', error);
    wiz.entryErrorEl = error;
    seg.setAttribute('aria-describedby', error.id);

    seg.addEventListener('click', function(e) {
      var cell = e.target.closest('.mw-segmented__cell');
      if (!cell) return;
      e.preventDefault();
      var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
      if (!checkbox) return;

      var target = cell.getAttribute('data-segment');
      setEntryChoice(target, true);
    });

    seg.addEventListener('keydown', function(e) {
      var cell = e.target && e.target.closest ? e.target.closest('.mw-segmented__cell') : null;
      if (!cell) return;
      var key = e.key || e.code;
      if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Left' && key !== 'Right') return;

      var cells = Array.prototype.slice.call(seg.querySelectorAll('.mw-segmented__cell'));
      if (cells.length < 2) return;
      e.preventDefault();

      var currentIndex = Math.max(0, cells.indexOf(cell));
      var nextIndex = (key === 'ArrowLeft' || key === 'Left')
        ? (currentIndex - 1 + cells.length) % cells.length
        : (currentIndex + 1) % cells.length;
      var next = cells[nextIndex];
      if (!next) return;

      if (next.focus) next.focus();
      setEntryChoice(next.getAttribute('data-segment'), true);
    });

    var initCheckbox = document.querySelector('[data-personalize-input][type="checkbox"]');
    if (initCheckbox && initCheckbox.checked && !getEntryChoice()) {
      initCheckbox.checked = false;
      initCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
    }

    installEntryCheckboxReconcile();
    installEntryChoiceGate();
    syncSegmentedControl();
  }

  function getEntryChoice() {
    if (wiz.entryChoice === 'personalize' || wiz.entryChoice === 'asis') return wiz.entryChoice;
    if (wiz.segmentedEl) {
      var val = wiz.segmentedEl.getAttribute('data-mw-entry-choice');
      if (val === 'personalize' || val === 'asis') return val;
    }
    var bodyVal = document.body ? document.body.getAttribute('data-mw-entry-choice') : '';
    return (bodyVal === 'personalize' || bodyVal === 'asis') ? bodyVal : '';
  }

  function clearEntryError() {
    if (wiz.segmentedEl) {
      wiz.segmentedEl.classList.remove('mw-segmented--required');
      wiz.segmentedEl.removeAttribute('aria-invalid');
    }
    if (wiz.entryErrorEl) wiz.entryErrorEl.style.display = 'none';
  }

  function showEntryError() {
    if (!wiz.segmentedEl) return;
    wiz.segmentedEl.classList.add('mw-segmented--required');
    wiz.segmentedEl.setAttribute('aria-invalid', 'true');
    if (wiz.entryErrorEl) wiz.entryErrorEl.style.display = '';
    try { wiz.segmentedEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
    var first = wiz.segmentedEl.querySelector('.mw-segmented__cell');
    if (first && first.focus) {
      setTimeout(function() { try { first.focus({ preventScroll: true }); } catch (e) { try { first.focus(); } catch (_) {} } }, 250);
    }
  }

  function isPersistentAlwaysVisibleOption(row) {
    if (!row) return false;
    if (row.classList && row.classList.contains('milano-always-visible')) return true;
    var opt = row.closest ? row.closest('.bold_option') : null;
    if (opt && opt.classList && opt.classList.contains('milano-always-visible')) return true;

    var name = '';
    if (opt) name = opt.getAttribute('data-option-name') || '';
    if (!name && row.getAttribute) name = row.getAttribute('data-option-name') || '';
    var title = row.querySelector ? row.querySelector('.bold_option_title, .milano-option-row__title') : null;
    if (!name && title) name = title.textContent || '';
    name = (name || '').toLowerCase().replace(/\s*[*:]+\s*$/, '').trim();

    return name === 'density' ||
           name.indexOf('cap size') > -1 ||
           name.indexOf('base size') > -1 ||
           name.indexOf('lace front') > -1;
  }

  function installEntryCheckboxReconcile() {
    var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
    if (!checkbox || checkbox.hasAttribute('data-mw-entry-reconcile')) return;
    checkbox.setAttribute('data-mw-entry-reconcile', '1');

    checkbox.addEventListener('change', function() {
      if (wiz.syncingEntryCheckbox) return;

      var choice = getEntryChoice();
      if (checkbox.checked) {
        if (choice !== 'personalize') setEntryChoice('personalize', false);
      } else if (choice === 'personalize') {
        // If something other than the new choice UI closes the hidden toggle,
        // treat it as the shopper returning to the as-pictured path. This keeps
        // the visible choice, hidden SC Options property, and wizard state aligned.
        setEntryChoice('asis', false);
      }
    });
  }

  function setEntryChoice(choice, shouldDispatch) {
    if (choice !== 'personalize' && choice !== 'asis') return;
    var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
    if (!checkbox) return;

    var previousChoice = getEntryChoice();
    wiz.entryChoice = choice;
    if (wiz.segmentedEl) wiz.segmentedEl.setAttribute('data-mw-entry-choice', choice);
    if (document.body) document.body.setAttribute('data-mw-entry-choice', choice);
    clearEntryError();

    if (choice === 'personalize') {
      if (!checkbox.checked) checkbox.checked = true;
      applyPersonalizeDefaultsOnce();
    } else {
      // Always clear wizard selections when the shopper chooses the as-pictured path.
      // This intentionally does NOT depend on the hidden Personalize checkbox state,
      // because SC Options/browser restores can occasionally leave paid inputs checked
      // after the toggle itself has already been reset.
      clearAllWizardSelections();
      checkbox.checked = false;
      for (var i = 0; i < wiz.steps.length; i++) wiz.steps[i].viewed = false;
      wiz.reviewed = false;
      wiz.virtualCutSelected = false;
      wiz.virtualColorSelected = false;
      if (wiz.reviewCardEl) wiz.reviewCardEl.style.display = 'none';
      wiz.currentStep = 0;
    }

    syncSegmentedControl();
    togglePersonalizeDesc();
    updateToggleTotal();
    updateWizardTotal();
    updatePriceContext();
    requestPDPPriceSync(previousChoice === choice ? 'entry-choice-reaffirmed' : 'entry-choice');

    if (shouldDispatch) {
      wiz.syncingEntryCheckbox = true;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      if (window.jQuery) {
        try { window.jQuery(checkbox).trigger('change'); } catch (e) { /* ignore */ }
      }
      setTimeout(function() { wiz.syncingEntryCheckbox = false; }, 0);
    }
  }

  function requireEntryChoiceBeforeCart() {
    if (!wiz.segmentedEl || wiz.isAccessory) return true;
    if (getEntryChoice()) return true;
    // v2026-08-07 (merchant decision): no Yes/No answered at add-to-cart ->
    // auto-select "No / Ship As Pictured" through the card's own click handler
    // (keeps every side effect consistent: aria-checked, entry state, the
    // properties[Personalize It] payload) and let the add proceed. A shopper
    // who chose Yes keeps whatever selections they made. Error+block remains
    // only as the defensive fallback if the programmatic click didn't take.
    try {
      var asisCell = wiz.segmentedEl.querySelector('.mw-segmented__cell--asis');
      if (asisCell) {
        asisCell.click();
        clearEntryError();
      }
    } catch (e) { /* fall through to the error path */ }
    if (getEntryChoice()) return true;
    showEntryError();
    return false;
  }

  function getCartAddFormFromTarget(target) {
    if (!target) return null;
    if (target.matches && target.matches('form[action*="/cart/add"]')) return target;
    if (target.form && target.form.matches && target.form.matches('form[action*="/cart/add"]')) return target.form;
    return target.closest ? target.closest('form[action*="/cart/add"]') : null;
  }

  // True ONLY for the page's main product Add-to-Cart form. Quick-add cards also
  // carry data-product-form (but add data-quickbuy-form); upsell/complementary
  // cards use data-product-form-upsell; Globo + theme quick-view live in their own
  // containers. The entry-choice gate must run only for the main form — gating a
  // recommendation/upsell card's own /cart/add form was firing THIS product's
  // "please choose one option" error and scrolling the page up to the wizard.
  function isMainProductForm(form) {
    if (!form || !form.hasAttribute) return false;
    if (!form.hasAttribute('data-product-form')) return false;       // upsell form lacks this attr
    if (form.hasAttribute('data-quickbuy-form')) return false;       // collection/related quick-add card
    if (form.closest && form.closest('.product-upsell, [data-upsell-holder], .product-grid-item, [data-recently-viewed-wrapper], [data-pair-products], .gfqv-modal, .gfqv-product-wrapper, [data-button-quick-view]')) return false;
    return true;
  }

  function getMainProductForm() {
    var forms = document.querySelectorAll('form[data-product-form][action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      if (isMainProductForm(forms[i])) return forms[i];
    }
    return null;
  }

  function hasValidVariantId(form) {
    if (!form || !form.querySelector) return false;
    var idInput = form.querySelector('[name="id"]');
    return !!(idInput && String(idInput.value || '').trim());
  }

  function normalizeQuantityBeforeSubmit(form) {
    if (!form || !form.querySelector) return true;
    var qty = form.querySelector('[name="quantity"]');
    if (!qty) return true;

    var min = parseInt(qty.getAttribute('min') || '1', 10);
    if (!min || min < 1) min = 1;

    var current = parseInt(qty.value || String(min), 10);
    if (!current || current < min) {
      qty.value = String(min);
    }

    return true;
  }

  function hasWizardOnlySelections() {
    var rows = document.querySelectorAll('.milano-panel .milano-option-row');
    for (var r = 0; r < rows.length; r++) {
      if (isPersistentAlwaysVisibleOption(rows[r])) continue;
      if (rows[r].querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) return true;

      var selects = rows[r].querySelectorAll('select');
      for (var s = 0; s < selects.length; s++) {
        if (selects[s].selectedIndex > 0) return true;
      }
    }
    return false;
  }

  // ── Fully-clean checkout (2026-06-25) ──────────────────────────────────
  // At submit, disable any SC option property input whose value is EMPTY (the
  // off-band / unselected groups SC otherwise serializes as blank line-item
  // properties). Empty == no selection == $0, so ZERO price impact; inputs are
  // re-enabled on the next tick so subsequent submits are unaffected. No-op if
  // SC builds its /cart/add payload from its own model rather than the form.
  // Gated to Grace; NEVER blocks ATC. Kill-switch: window.MILANO_DISABLE_STRIP.
  function mwStripEmptyProps(form) {
    if (!form || window.MILANO_DISABLE_STRIP) return;
    if (!document.body.classList.contains('milano-grace')) return;
    var fields = form.querySelectorAll('input[name^="properties["], select[name^="properties["], textarea[name^="properties["]');
    for (var i = 0; i < fields.length; i++) {
      var el = fields[i];
      if (el.disabled) continue;
      if (el.type === 'checkbox' || el.type === 'radio') continue; // unchecked ones never serialize anyway
      if (el.value && String(el.value).trim() !== '') continue;    // carries a real selection — never strip
      el.disabled = true;
      el.setAttribute('data-mw-stripped', '1');
    }
  }
  function mwRestoreStrippedProps() {
    var stripped = document.querySelectorAll('[data-mw-stripped]');
    for (var i = 0; i < stripped.length; i++) {
      stripped[i].disabled = false;
      stripped[i].removeAttribute('data-mw-stripped');
    }
  }

  function prepareCartSubmitPayload(form) {
    if (!form) return false;

    if (typeof window.MILANO_prepareProductFormForCart === 'function') {
      try { window.MILANO_prepareProductFormForCart(form); } catch (e) { /* non-critical; validation below still runs */ }
    }

    if (!hasValidVariantId(form)) {
      if (window.MILANO_DEBUG) console.warn('[Milano Wizard] Blocked cart submit: missing variant id');
      return false;
    }

    normalizeQuantityBeforeSubmit(form);

    // v1.6.47: final enforcement — re-apply Grace length-band visibility
    // (which also clears selections inside a length-hidden banded group) so
    // the submitted SC payload can never carry the wrong band's priced
    // option, regardless of async re-render timing.
    if (document.body.classList.contains('milano-grace')) {
      try { applyLengthVisibility(); } catch (e) { /* never block ATC on this */ }
    }

    if (!requireEntryChoiceBeforeCart()) return false;

    if (getEntryChoice() === 'asis' && hasWizardOnlySelections()) {
      // Last-resort cleanup for browser restores/app timing. The function is
      // already scoped to skip Cap Size / Lace Front / other always-visible
      // setup choices, so it only removes personalization-only payload.
      clearAllWizardSelections();
    }

    // Fully-clean checkout: disable empty/unselected SC option properties so they
    // don't serialize as blank line-item properties; re-enabled on the next tick.
    try { mwStripEmptyProps(form); setTimeout(mwRestoreStrippedProps, 0); } catch (e) { try { mwRestoreStrippedProps(); } catch (_) {} }

    return true;
  }

  // v2026-08-06b: ATC click gate registered at SCRIPT LOAD, not wizard init -
  // merchant QA showed adds slipping through when only the submit-event gate
  // (or a late-installed click gate) stood in the way. Runs at document
  // capture, before any theme click/submit listener; harmlessly allows
  // everything until the wizard's entry cards exist (requireEntryChoiceBeforeCart
  // returns true when wiz.segmentedEl is null).
  function mwAtcClickGate(e) {
    try {
      var btn = e.target && e.target.closest ? e.target.closest('[data-add-to-cart]') : null;
      if (!btn) return;
      var form = getCartAddFormFromTarget(btn);
      if (!form || !isMainProductForm(form)) return;
      if (requireEntryChoiceBeforeCart()) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    } catch (err) { /* never block ATC on a gate error */ }
  }
  if (!window.__mwAtcClickGate) {
    window.__mwAtcClickGate = true;
    document.addEventListener('click', mwAtcClickGate, true);
  }

  function installEntryChoiceGate() {
    if (wiz.entryGateInstalled) return;
    wiz.entryGateInstalled = true;

    document.addEventListener('submit', function(e) {
      var form = getCartAddFormFromTarget(e.target);
      if (!form) return;
      // Only the MAIN product form passes through the entry-choice gate.
      // Recommendation / upsell / complementary / quick-add / quick-view cards
      // each post their own /cart/add form; gating them fired this product's
      // validation and scrolled to the main wizard.
      if (!isMainProductForm(form)) return;
      if (!prepareCartSubmitPayload(form)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    document.addEventListener('click', function(e) {
      var btn = e.target && e.target.closest ? e.target.closest('button, input[type="submit"], [type="submit"]') : null;
      if (!btn) return;
      // Native utility controls such as Size guide, Compare, drawer toggles,
      // and modal triggers are intentionally type="button". They never submit
      // the product form, so the cart-entry gate must not intercept them.
      // A <button> with no type is still treated as submit-capable by HTML and
      // continues through the gate for backwards compatibility.
      if (btn.matches && btn.matches('button[type="button"], button[type="reset"]')) return;
      // Do not gate the entry-choice buttons themselves. This listener runs in
      // capture phase, so gating them here would prevent their own click handler
      // from setting the choice and opening the wizard.
      if (btn.closest && btn.closest('.mw-segmented')) return;
      // Do not gate the consult CTAs ("Contact Us" / "Free Consult") that sit
      // beneath ATC. They live inside the .product__submit area, so without this
      // guard the capture-phase gate fires the entry-choice error and
      // stopPropagation() swallows their own click handler (open contact / nav).
      // Only Add to Cart should require an entry choice.
      if (btn.closest && btn.closest('.mw-consult')) return;

      // Never gate a recommendation / upsell / complementary / quick-add /
      // quick-view / Globo-modal card button — each manages its own product form
      // and must not trigger THIS product's personalization validation or scroll.
      if (btn.closest && btn.closest('.product-upsell, [data-upsell-holder], .product-grid-item, [data-recently-viewed-wrapper], [data-pair-products], .gfqv-modal, .gfqv-product-wrapper, [data-button-quick-view]')) return;

      var form = getCartAddFormFromTarget(btn);
      if (form && !isMainProductForm(form)) return;
      if (!form && !btn.closest('.product__submit, .product__submit__buttons, .product__submit__holder')) return;
      if (!form) form = getMainProductForm();

      if (!prepareCartSubmitPayload(form)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }

  function applyPersonalizeDefaultsOnce() {
    if (wiz.personalizeDefaultsApplied) return;
    wiz.personalizeDefaultsApplied = true;
    autoSelectDefaults();
    autoSelectRecommended();
    injectVirtualCutSkip();
    injectVirtualColorSkip();
    injectVirtualStyleSkip();
    compactShortOptionGroups();
  }

  function syncSegmentedControl() {
    if (!wiz.segmentedEl) return;
    var choice = getEntryChoice();

    var asis = wiz.segmentedEl.querySelector('[data-segment="asis"]');
    var pers = wiz.segmentedEl.querySelector('[data-segment="personalize"]');
    if (asis) {
      var asisActive = choice === 'asis';
      asis.classList.toggle('mw-segmented__cell--active', asisActive);
      asis.setAttribute('aria-checked', String(asisActive));
      asis.setAttribute('tabindex', choice ? (asisActive ? '0' : '-1') : '0');
    }
    if (pers) {
      var persActive = choice === 'personalize';
      pers.classList.toggle('mw-segmented__cell--active', persActive);
      pers.setAttribute('aria-checked', String(persActive));
      pers.setAttribute('tabindex', choice ? (persActive ? '0' : '-1') : '0');
    }

    document.body.classList.toggle('mw-personalize-active', choice === 'personalize');
    document.body.classList.toggle('mw-entry-choice-made', !!choice);
  }

  // =========================================================================
  // PERSONALIZE DESCRIPTION (v1.6.42 copy)
  // =========================================================================

  function buildPersonalizeDesc() {
    var toggleEl = document.querySelector('.personalize-toggle');
    if (!toggleEl || toggleEl.querySelector('.mw-personalize-desc')) return;

    var prodTypeLow = wiz.isTopper ? 'topper' : 'wig';

    var wrap = document.createElement('div');
    wrap.className = 'mw-personalize-desc';
    // v1.6.42: leads with the ships-as-pictured promise (low-risk reassurance)
    // then invites adjustment (call to action). Mobile condensed to fit one
    // line at iPhone 14 Pro Max width / 12.5px font.
    wrap.innerHTML =
      '<span class="mw-pd-mobile">Choose only the details you want to add.</span>' +
      '<span class="mw-pd-desktop">Choose only the details you want to add. Everything is optional and guided step by step.</span>';

    var seg = toggleEl.querySelector('.mw-segmented');
    if (seg) seg.insertAdjacentElement('afterend', wrap);
    else toggleEl.insertBefore(wrap, toggleEl.firstChild);

    wiz.personalizeDescEl = wrap;
    togglePersonalizeDesc();
  }

  function togglePersonalizeDesc() {
    if (!wiz.personalizeDescEl) return;
    var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
    var show = !!(checkbox && checkbox.checked);
    wiz.personalizeDescEl.classList.toggle('mw-personalize-desc--visible', show);
  }

  // =========================================================================
  // CLEAR ALL WIZARD SELECTIONS
  // =========================================================================

  function clearAllWizardSelections() {
    var rows = document.querySelectorAll('.milano-panel .milano-option-row');
    var cleared = 0;
    var hasJQ = !!window.jQuery;
    var clearedInputs = hasJQ ? [] : null;
    var clearedSelects = hasJQ ? [] : null;

    for (var r = 0; r < rows.length; r++) {
      // Ship As Pictured should clear only wizard/personalization selections.
      // Keep persistent setup choices such as Cap Size intact, even if an app
      // or browser restore temporarily places them inside the wizard panel.
      if (isPersistentAlwaysVisibleOption(rows[r])) continue;

      var inputs = rows[r].querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
      for (var i = 0; i < inputs.length; i++) {
        inputs[i].checked = false;
        inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
        if (hasJQ) clearedInputs.push(inputs[i]);
        cleared++;
      }
      var selects = rows[r].querySelectorAll('select');
      for (var s = 0; s < selects.length; s++) {
        if (selects[s].selectedIndex > 0) {
          selects[s].selectedIndex = 0;
          selects[s].dispatchEvent(new Event('change', { bubbles: true }));
          if (hasJQ) clearedSelects.push(selects[s]);
          cleared++;
        }
      }
    }

    wiz.virtualCutSelected = false;
    wiz.virtualColorSelected = false;
    wiz.personalizeDefaultsApplied = false;
    var virtualCutCard = document.querySelector('.mw-no-cut-virtual');
    if (virtualCutCard) {
      virtualCutCard.classList.remove('mw-no-cut-virtual--selected');
      virtualCutCard.setAttribute('aria-pressed', 'false');
    }
    var virtualColorCard = document.querySelector('.mw-no-color-virtual');
    if (virtualColorCard) {
      virtualColorCard.classList.remove('mw-no-color-virtual--selected');
      virtualColorCard.setAttribute('aria-pressed', 'false');
    }

    if (window.jQuery) {
      setTimeout(function() {
        try {
          var $ = window.jQuery;
          for (var ci = 0; ci < clearedInputs.length; ci++) $(clearedInputs[ci]).trigger('change');
          for (var cs = 0; cs < clearedSelects.length; cs++) $(clearedSelects[cs]).trigger('change');
        } catch (e) { /* ignore */ }
      }, 150);
    }

    setTimeout(function() {
      updateCompletion();
      updateToggleTotal();
      updatePriceContext();
      updateWizardTotal();
      if (wiz.reviewed) updateReviewCard();
    }, 100);

    log('[Milano Wizard] Cleared ' + cleared + ' selection(s) \u2014 back to as pictured');
  }

  // =========================================================================
  // VIRTUAL "KEEP AS PICTURED" CARDS
  // =========================================================================

  function _injectVirtualSkip(cfg) {
    var step = null;
    for (var i = 0; i < wiz.steps.length; i++) {
      if (wiz.steps[i].id === cfg.stepId) { step = wiz.steps[i]; break; }
    }
    if (!step || step.rows.length === 0) return;

    for (var r = 0; r < step.rows.length; r++) {
      if (step.rows[r].querySelector('.' + cfg.nativeClass)) return;
    }

    var hostRow = null;
    for (var rr = 0; rr < step.rows.length; rr++) {
      if (step.rows[rr].classList.contains('milano-option-row--hidden')) continue;
      if (cfg.skipSubgates) {
        if (step.rows[rr].classList.contains('mw-subgate')) continue;
        var rowName = (step.rows[rr].getAttribute('data-option-name') || '').toLowerCase();
        if (rowName.indexOf('position of') > -1 || rowName.indexOf('bangs or fringe') > -1) continue;
      }
      hostRow = step.rows[rr];
      break;
    }
    if (!hostRow) return;
    var body = hostRow.querySelector('.milano-option-row__body');
    if (!body || body.querySelector('.' + cfg.virtualClass)) return;

    var card = document.createElement('div');
    card.className = cfg.virtualClass;
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.setAttribute('aria-pressed', 'false');

    var html =
      '<span class="' + cfg.virtualClass + '__check" aria-hidden="true">' +
        '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</span>' +
      '<span class="' + cfg.virtualClass + '__label">' + cfg.copy.virtualLabel + '</span>';

    if (cfg.copy.virtualSub) {
      var subText = typeof cfg.copy.virtualSub === 'function'
        ? cfg.copy.virtualSub()
        : cfg.copy.virtualSub;
      html += '<span class="' + cfg.virtualClass + '__sub">' + subText + '</span>';
    }

    card.innerHTML = html;
    body.insertBefore(card, body.firstChild);

    if (!card.nextElementSibling || !card.nextElementSibling.classList.contains(cfg.sepClass)) {
      var sep = document.createElement('div');
      sep.className = cfg.sepClass;
      sep.textContent = cfg.copy.sepLabel;
      card.parentNode.insertBefore(sep, card.nextSibling);
    }

    function activate() {
      for (var s = 0; s < step.rows.length; s++) {
        var inputs = step.rows[s].querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
        for (var ii = 0; ii < inputs.length; ii++) {
          inputs[ii].checked = false;
          inputs[ii].dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      wiz[cfg.flag] = true;
      card.classList.add(cfg.virtualClass + '--selected');
      card.setAttribute('aria-pressed', 'true');
    }

    card.addEventListener('click', function(e) {
      e.preventDefault();
      if (!card.classList.contains(cfg.virtualClass + '--selected')) activate();
    });
    card.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (!card.classList.contains(cfg.virtualClass + '--selected')) activate();
      }
    });

    _syncVirtualSkip(cfg);
  }

  function _syncVirtualSkip(cfg) {
    var card = document.querySelector('.' + cfg.virtualClass);
    if (!card) return;

    var step = null;
    for (var i = 0; i < wiz.steps.length; i++) {
      if (wiz.steps[i].id === cfg.stepId) { step = wiz.steps[i]; break; }
    }
    if (!step) return;

    var hasRealSelection = false;
    for (var r = 0; r < step.rows.length; r++) {
      if (step.rows[r].classList.contains('milano-option-row--hidden')) continue;
      if (step.rows[r].querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) {
        hasRealSelection = true;
        break;
      }
      var selects = step.rows[r].querySelectorAll('select');
      for (var s = 0; s < selects.length; s++) {
        if (selects[s].selectedIndex > 0) { hasRealSelection = true; break; }
      }
      if (hasRealSelection) break;
    }

    if (hasRealSelection) {
      wiz[cfg.flag] = false;
      card.classList.remove(cfg.virtualClass + '--selected');
      card.setAttribute('aria-pressed', 'false');
    } else {
      wiz[cfg.flag] = true;
      card.classList.add(cfg.virtualClass + '--selected');
      card.setAttribute('aria-pressed', 'true');
    }
  }

  function injectVirtualCutSkip()   { _injectVirtualSkip(VIRTUAL_SKIP_CONFIGS.cut);    }
  function syncVirtualCutSkip()     { _syncVirtualSkip(VIRTUAL_SKIP_CONFIGS.cut);      }
  function injectVirtualColorSkip() { _injectVirtualSkip(VIRTUAL_SKIP_CONFIGS.color);  }
  function syncVirtualColorSkip()   { _syncVirtualSkip(VIRTUAL_SKIP_CONFIGS.color);    }

  // =========================================================================
  // "KEEP AS PICTURED" VIRTUAL CARD — Style + Part Location (v2026-06-22)
  // Some Style / Part Location SC-option sets have no explicit "as is" choice,
  // so there is no visible default. Inject a DISPLAY-ONLY "Keep as pictured"
  // card per group (only when the group lacks such an option). Selecting it just
  // clears that group's inputs — identical to choosing nothing (no behavior
  // change); it only makes the as-pictured default explicit. Per-GROUP (not
  // per-step like cut/color) because the Style step holds two independent groups.
  // =========================================================================
  function _syncKeepCard(row, card) {
    card = card || row.querySelector('.mw-keep-virtual');
    if (!card) return;
    var hasSel = !!row.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
    if (!hasSel) {
      var sels = row.querySelectorAll('select');
      for (var s = 0; s < sels.length; s++) { if (sels[s].selectedIndex > 0) { hasSel = true; break; } }
    }
    if (hasSel) { card.classList.remove('mw-keep-virtual--selected'); card.setAttribute('aria-pressed', 'false'); }
    else { card.classList.add('mw-keep-virtual--selected'); card.setAttribute('aria-pressed', 'true'); }
  }

  function _injectKeepCard(row) {
    if (!row || row.classList.contains('milano-option-row--hidden') || row.classList.contains('mw-subgate')) return;
    var body = row.querySelector('.milano-option-row__body');
    if (!body || body.querySelector('.mw-keep-virtual')) return;
    // Skip if the group already offers an as-pictured / keep / no-* choice.
    var labels = body.querySelectorAll('.bold_option_value label');
    if (labels.length === 0) return;
    for (var i = 0; i < labels.length; i++) {
      if (isSkipDefaultLabel(stripPriceText(labels[i].textContent))) return;
    }
    var card = document.createElement('div');
    card.className = 'mw-keep-virtual';
    card.setAttribute('role', 'button');
    card.setAttribute('tabindex', '0');
    card.setAttribute('aria-pressed', 'false');
    card.innerHTML =
      '<span class="mw-keep-virtual__check" aria-hidden="true">' +
        '<svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</span>' +
      '<span class="mw-keep-virtual__label">Keep As Pictured</span>';
    // Insert as the FIRST option IN the set (right after the group title, before
    // the real radios) so it reads as part of the option set, not a separate card.
    var mwFirstOpt = body.querySelector('.bold_option_value');
    if (mwFirstOpt && mwFirstOpt.parentNode) { mwFirstOpt.parentNode.insertBefore(card, mwFirstOpt); }
    else { body.insertBefore(card, body.firstChild); }

    function clearRow() {
      var inputs = row.querySelectorAll('input[type="radio"]:checked, input[type="checkbox"]:checked');
      for (var ii = 0; ii < inputs.length; ii++) {
        inputs[ii].checked = false;
        inputs[ii].dispatchEvent(new Event('change', { bubbles: true }));
      }
      var sels = row.querySelectorAll('select');
      for (var s = 0; s < sels.length; s++) {
        if (sels[s].selectedIndex > 0) { sels[s].selectedIndex = 0; sels[s].dispatchEvent(new Event('change', { bubbles: true })); }
      }
    }
    card.addEventListener('click', function(e) { e.preventDefault(); clearRow(); _syncKeepCard(row, card); });
    card.addEventListener('keydown', function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); clearRow(); _syncKeepCard(row, card); } });
    // Keep the card's selected state in sync when a real option in this group changes.
    row.addEventListener('change', function() { _syncKeepCard(row, card); });
    _syncKeepCard(row, card);
  }

  function injectVirtualStyleSkip() {
    var step = null;
    for (var i = 0; i < wiz.steps.length; i++) { if (wiz.steps[i].id === 3) { step = wiz.steps[i]; break; } }
    if (!step) return;
    for (var r = 0; r < step.rows.length; r++) {
      var row = step.rows[r];
      var name = (row.getAttribute('data-option-name') || '').toLowerCase();
      if (name.indexOf('style') > -1 || name.indexOf('part') > -1 || name.indexOf('location') > -1) {
        _injectKeepCard(row);
      }
    }
  }

  // v2026-06-22: short yes/no-style option groups stack as full-width rows and
  // waste vertical space on mobile. Tag the values container of groups whose
  // options are ALL short (<=18 chars, no price/badge/inline-desc, 2-4 options)
  // so CSS can flow them two-up on small screens. Display-only.
  function compactShortOptionGroups() {
    var rows = document.querySelectorAll('.milano-panel .milano-option-row');
    for (var r = 0; r < rows.length; r++) {
      var body = rows[r].querySelector('.milano-option-row__body');
      if (!body) continue;
      var opts = body.querySelectorAll('.bold_option_value');
      if (opts.length < 2 || opts.length > 4) continue;
      var container = opts[0].parentNode;
      if (!container || container.classList.contains('mw-options-grid')) continue;
      var ok = true;
      for (var i = 0; i < opts.length; i++) {
        if (opts[i].querySelector('.bold_option_value_price, .mw-badge, .mw-opt-inline-desc')) { ok = false; break; }
        var lbl = opts[i].querySelector('label');
        var txt = lbl ? stripPriceText(lbl.textContent) : '';
        if (txt.length === 0 || txt.length > 18) { ok = false; break; }
      }
      if (ok) container.classList.add('mw-options-grid');
    }
  }

  // Itemization (2026-08-06, merchant-approved): split priced ALWAYS-VISIBLE
  // setup choices (Cap Size etc. — product configuration surcharges) from true
  // personalization services, so "+$100" from a cap pick is never presented as
  // a service. Radio de-dupe mirrors getServicesDollars' fallback path.
  function getSetupOptionsBreakdown() {
    var extras = [];
    var radioByGroup = {};
    var checked = document.querySelectorAll('.bold_option.milano-always-visible input:checked');
    for (var i = 0; i < checked.length; i++) {
      var input = checked[i];
      if (input.disabled) continue;
      var row = input.closest('.bold_option');
      if (!row) continue;
      var valueEl = input.closest('.bold_option_value') || input.closest('label') || row;
      var priceEl = valueEl ? valueEl.querySelector('.bold_option_value_price') : null;
      if (!priceEl) continue;
      var c = Math.round((parseFloat((priceEl.textContent || '').replace(/,/g, '').replace(/[^\d.]/g, '')) || 0) * 100);
      if (c <= 0) continue;
      var groupName = (row.getAttribute('data-option-name') || '').trim();
      var groupKey = groupName.toLowerCase() || (input.name || '') || ('__row-' + i);
      if ((input.type || '').toLowerCase() === 'radio') {
        var shown = !!(input.getClientRects && input.getClientRects().length > 0);
        var prev = radioByGroup[groupKey];
        if (!prev || shown || !prev.shown) radioByGroup[groupKey] = { name: groupName, cents: c, shown: shown };
      } else {
        extras.push({ name: groupName, dollars: Math.round(c / 100) });
      }
    }
    for (var rg in radioByGroup) {
      if (radioByGroup.hasOwnProperty(rg)) {
        extras.push({ name: radioByGroup[rg].name, dollars: Math.round(radioByGroup[rg].cents / 100) });
      }
    }
    return extras;
  }

  function getSetupOptionsDollars() {
    var extras = getSetupOptionsBreakdown();
    var dollars = 0;
    for (var i = 0; i < extras.length; i++) dollars += extras[i].dollars;
    return dollars;
  }

  function getActualServicesDollars() {
    // Services-only: the wizard's "+$" chips and paid-service notices must not
    // count setup surcharges (the old lump showed "+$100" from a cap pick).
    var services = getServicesDollars() - getSetupOptionsDollars();
    return services > 0 ? services : 0;
  }


  function updateToggleTotal() {
    if (!wiz.toggleTotalEl) return;
    var dollars = getActualServicesDollars();
    var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
    var panelOpen = checkbox && checkbox.checked;
    if (dollars > 0 && !panelOpen) {
      wiz.toggleTotalEl.textContent = '+$' + dollars;
      wiz.toggleTotalEl.style.display = 'inline-block';
    } else {
      wiz.toggleTotalEl.style.display = 'none';
    }
  }

  function updateWizardTotal() {
    if (!wiz.wizTotalEl) return;
    var dollars = getActualServicesDollars();
    if (dollars > 0) {
      wiz.wizTotalEl.textContent = '+$' + dollars;
      wiz.wizTotalEl.style.display = 'inline-block';
    } else {
      wiz.wizTotalEl.style.display = 'none';
    }
  }

  function watchToggleTitle() {
    var titleEl = document.querySelector('.personalize-toggle__title');
    var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
    if (!titleEl || !checkbox) return;
    var subtitleEl = document.querySelector('.personalize-toggle__subtitle');

    function update() {
      var prodType = wiz.isTopper ? 'Topper' : 'Wig';
      titleEl.textContent = checkbox.checked ? 'Personalizing Your ' + prodType : 'Choose How It Ships';
      if (subtitleEl) subtitleEl.textContent = checkbox.checked
        ? 'All optional \u00B7 Tap any step to jump'
        : 'Personalize details or Ship As Pictured';
      updateToggleTotal();
      syncSegmentedControl();
      togglePersonalizeDesc();
    }
    update();
    checkbox.addEventListener('change', update);
  }

  // =========================================================================
  // AUTO-SELECT CAP SIZE (v1.6.42: dead helperText refs removed)
  // =========================================================================

  function autoSelectCapSize() {
    var capEl = null;
    var allVisible = document.querySelectorAll('.milano-always-visible');
    for (var i = 0; i < allVisible.length; i++) {
      var t = allVisible[i].querySelector('.bold_option_title');
      var name = t ? (t.textContent || '').trim().toLowerCase() : '';
      if (name.indexOf('cap size') > -1) { capEl = allVisible[i]; break; }
    }
    if (!capEl) return false;

    // If something is already checked, we're done — don't override user choice.
    // v1.6.42: removed stray `helperText` reference (variable never defined;
    // .mw-cap-helper element was deleted in v1.6.13 so the if-guard always
    // fell through, but the read would have thrown if it ever entered).
    var preChecked = capEl.querySelector('input[type="radio"]:checked');
    if (preChecked) return true;

    var collectionKey = detectCollection();
    var target = 'medium';

    if (collectionKey && CAP_SIZE_MAP[collectionKey]) {
      target = CAP_SIZE_MAP[collectionKey].size;
    }

    var radios = capEl.querySelectorAll('input[type="radio"]');
    if (!radios.length) return false;
    var selected = false;
    for (var r = 0; r < radios.length; r++) {
      var label = radios[r].parentElement.querySelector('label') || radios[r].nextElementSibling;
      if (!label) continue;
      var labelText = (label.textContent || '').trim().toLowerCase();
      if (labelText.indexOf(target) > -1 && !radios[r].checked) {
        radios[r].checked = true;
        radios[r].dispatchEvent(new Event('change', { bubbles: true }));
        selected = true;
        break;
      }
    }

    return selected;
  }

  // =========================================================================
  // FORMAT WIZARD-OWNED PRICES
  //
  // Keep this intentionally narrow. Discount Ninja and Palo Alto own the PDP,
  // compare, sticky, and cart-bar price DOM and must retain their exact money
  // formatting (including trailing .00 cents).
  // =========================================================================

  function stripTrailingZeroCents(text) {
    return String(text || '')
      .replace(/(\$[\s ]*[0-9][0-9,]*)\.00\b/g, '$1')
      .replace(/(\+\s*\$[\s ]*[0-9][0-9,]*)\.00\b/g, '$1')
      .replace(/(-\s*\$[\s ]*[0-9][0-9,]*)\.00\b/g, '$1');
  }

  function stripTrailingZeroCentsIn(el) {
    if (!el || el.querySelector && el.querySelector('.mw-included')) return;

    try {
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var nodes = [];
      var node;
      while ((node = walker.nextNode())) nodes.push(node);
      for (var n = 0; n < nodes.length; n++) {
        nodes[n].nodeValue = stripTrailingZeroCents(nodes[n].nodeValue);
      }
    } catch (_) {
      if (el.textContent) el.textContent = stripTrailingZeroCents(el.textContent);
    }
  }

  function formatWizardPrices() {
    var prices = document.querySelectorAll(
      [
        '.milano-option-row__body .bold_option_value_price',
        '.milano-option-row__preview',
        '.bold_option.milano-always-visible .bold_option_value_price',
        '.milano-summary__item-price',
        '.mw-review-card__price',
        '.mw-review-card__total-line'
      ].join(',')
    );
    if (prices.length === 0) return;

    for (var p = 0; p < prices.length; p++) {
      var el = prices[p];
      if (el.querySelector('.mw-included')) continue;
      if (el.hasAttribute('data-mw-discounted')) continue;
      if (el.closest('[class*="limoniapps-discountninja"], [data-la-dn-price], [data-compare-price]')) continue;
      stripTrailingZeroCentsIn(el);
    }
  }

  // =========================================================================
  // COLOR EXCLUSIVITY
  // =========================================================================

  // v1.6.47: generalized to EVERY color group. The Grace union set renders
  // TWO color groups (short band + "Color Services - Processed"); the old
  // single-querySelector version wired only the first, leaving the other
  // group's "No Color" without exclusivity. One delegated listener per
  // group container: survives SC innerHTML re-renders (no direct element
  // bindings), idempotent via data-mw-color-excl, re-called from the change
  // debounce so late-classed groups get wired.
  function setupColorExclusivity() {
    var noColorEls = document.querySelectorAll('.mw-no-color');
    for (var n = 0; n < noColorEls.length; n++) {
      var parent = noColorEls[n].parentElement;
      if (!parent || parent.hasAttribute('data-mw-color-excl')) continue;
      parent.setAttribute('data-mw-color-excl', '1');

      (function(container) {
        container.addEventListener('change', function(e) {
          if (!e.target || e.target.type !== 'checkbox') return;
          var live = container.querySelector('.mw-no-color input[type="checkbox"]');
          if (!live) return;
          if (e.target === live) {
            if (!live.checked) return;
            var paid = container.querySelectorAll('.bold_option_value:not(.mw-no-color) input[type="checkbox"]');
            for (var i = 0; i < paid.length; i++) {
              if (paid[i].checked) {
                paid[i].checked = false;
                paid[i].dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          } else if (e.target.checked && live.checked) {
            live.checked = false;
            live.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      })(parent);
    }
  }

  // =========================================================================
  // AUTO-BYPASS COLOR SERVICE GATE
  // =========================================================================

  function autoBypassColorGate() {
    var allOptions = document.querySelectorAll('.milano-option-row .bold_option');
    for (var i = 0; i < allOptions.length; i++) {
      var name = (allOptions[i].getAttribute('data-option-name') || '').toLowerCase();
      if (!name) {
        var titleEl = allOptions[i].querySelector('.bold_option_title');
        name = titleEl ? (titleEl.textContent || '').trim().toLowerCase() : '';
      }
      if (name.indexOf('add color') > -1 || name.indexOf('color service') > -1) {
        var select = allOptions[i].querySelector('select');
        if (select) {
          for (var o = 0; o < select.options.length; o++) {
            if (select.options[o].value.toLowerCase() === 'yes' || select.options[o].text.toLowerCase() === 'yes') {
              select.selectedIndex = o;
              select.dispatchEvent(new Event('change', { bubbles: true }));
              var gateRow = allOptions[i].closest('.milano-option-row');
              if (gateRow) gateRow.style.display = 'none';
              log('[Milano Wizard] Color gate auto-bypassed');
              break;
            }
          }
        }
      }
    }
  }

  // =========================================================================
  // AUTO-SELECT DEFAULTS
  // =========================================================================

  function autoSelectDefaults() {
    var allValues = document.querySelectorAll('.milano-option-row__body .bold_option_value');
    var processedRows = [];
    var selected = 0;

    for (var i = 0; i < allValues.length; i++) {
      var lbl = allValues[i].querySelector('label');
      if (!lbl) continue;
      var text = stripPriceText(lbl.textContent);
      if (!isSkipDefaultLabel(text)) continue;

      var optionRow = allValues[i].closest('.milano-option-row');
      if (!optionRow) continue;
      // v1.6.47: never default a hidden row — a length-hidden group must not
      // be re-checked into the cart payload.
      if (optionRow.classList.contains('milano-option-row--hidden')) continue;
      var rowId = optionRow.getAttribute('data-option-name') || '';
      if (processedRows.indexOf(rowId) > -1) continue;
      processedRows.push(rowId);

      var body = optionRow.querySelector('.milano-option-row__body');
      if (!body) continue;
      if (body.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) continue;
      var hasSelectedDropdown = false;
      var selects = body.querySelectorAll('select');
      for (var s = 0; s < selects.length; s++) {
        if (selects[s].selectedIndex > 0) { hasSelectedDropdown = true; break; }
      }
      if (hasSelectedDropdown) continue;

      var input = allValues[i].querySelector('input[type="radio"], input[type="checkbox"]');
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        selected++;
      }
    }

    if (selected > 0) {
      log('[Milano Wizard] Auto-selected ' + selected + ' default(s)');
    }
  }

  // =========================================================================
  // AUTO-SELECT RECOMMENDED
  // =========================================================================

  function autoSelectRecommended() {
    var badges = document.querySelectorAll('.milano-option-row__body .mw-badge');
    var selected = 0;

    for (var b = 0; b < badges.length; b++) {
      if (badges[b].textContent.trim() !== 'Recommended') continue;

      var optVal = badges[b].closest('.bold_option_value');
      if (!optVal) continue;

      var optRow = optVal.closest('.milano-option-row');
      if (!optRow) continue;
      if (optRow.classList.contains('milano-option-row--hidden')) continue;

      var body = optRow.querySelector('.milano-option-row__body');
      if (!body) continue;
      if (body.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked')) continue;

      var priceEl = optVal.querySelector('.bold_option_value_price');
      var priceText = priceEl ? (priceEl.textContent || '').replace(/[^\d.]/g, '') : '';
      var price = parseFloat(priceText) || 0;
      if (price > 0) {
        continue;
      }

      var input = optVal.querySelector('input[type="radio"], input[type="checkbox"]');
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        selected++;
      }
    }

    var allRows = document.querySelectorAll('.milano-option-row');
    for (var r = 0; r < allRows.length; r++) {
      if (allRows[r].classList.contains('milano-option-row--hidden')) continue;
      var fbName = (allRows[r].getAttribute('data-option-name') || '').toLowerCase();
      if (fbName.indexOf('secure') === -1) continue;

      var fbBody = allRows[r].querySelector('.milano-option-row__body');
      if (!fbBody) continue;
      if (fbBody.querySelector('input:checked')) continue;
      if (!fbBody.querySelector('input[type="radio"]')) continue;

      var fbVals = fbBody.querySelectorAll('.bold_option_value');
      for (var v = 0; v < fbVals.length; v++) {
        var fbPrice = fbVals[v].querySelector('.bold_option_value_price');
        var fbText = fbPrice ? (fbPrice.textContent || '').replace(/[^\d.]/g, '') : '';
        var fbAmt = parseFloat(fbText) || 0;
        if (fbAmt > 0) continue;
        var fbInput = fbVals[v].querySelector('input[type="radio"]');
        if (fbInput && !fbInput.checked) {
          fbInput.checked = true;
          fbInput.dispatchEvent(new Event('change', { bubbles: true }));
          selected++;
          break;
        }
      }
    }

    if (selected > 0) {
      log('[Milano Wizard] Auto-selected ' + selected + ' recommended/default option(s)');
    }
  }

  // =========================================================================
  // PRODUCT PRICE
  // =========================================================================

  function enhanceProductPrice() {
    var priceEl = document.querySelector('.product__price .money, [data-product-price] .money, .product__price-and-badge .money');
    if (!priceEl) priceEl = document.querySelector('[data-product-price]');
    if (priceEl && !priceEl.classList.contains('mw-price-enhanced')) {
      priceEl.classList.add('mw-price-enhanced');
    }
  }

  // =========================================================================
  // PRICE CONTEXT
  // =========================================================================

  function injectPriceContext() {
    if (document.querySelector('.mw-price-context')) return;
    var contextEl = document.createElement('span');
    contextEl.className = 'mw-price-context';
    contextEl.style.display = 'none';

    var priceWrapper = document.querySelector('[data-price-wrapper]:not([data-atc-button])') ||
                       document.querySelector('.product__price-and-badge');
    if (!priceWrapper) return;

    var priceEl = priceWrapper.querySelector('[data-product-price]');
    try {
      if (priceEl && priceEl.parentNode) {
        priceEl.insertAdjacentElement('afterend', contextEl);
      } else {
        priceWrapper.appendChild(contextEl);
      }
    } catch (_) {
      try { priceWrapper.appendChild(contextEl); } catch (__) {}
    }
  }


  function updatePriceContext() {
    var contextEl = document.querySelector('.mw-price-context');
    if (!contextEl) return;
    var dollars = getActualServicesDollars();

    var priceWrap = document.querySelector('.product__price-and-badge');
    if (priceWrap) priceWrap.classList.toggle('mw-has-services', dollars > 0);

    if (dollars > 0) {
      contextEl.textContent = 'Includes $' + dollars + ' in selected options';
      contextEl.style.display = '';
    } else {
      contextEl.style.display = 'none';
    }
  }

  // =========================================================================
  // REVIEW CARD
  // =========================================================================

  function buildReviewCard() {
    var card = document.createElement('div');
    card.className = 'mw-review-card';
    card.style.display = 'none';

    var panel = document.querySelector('.milano-panel');
    if (panel) panel.parentNode.insertBefore(card, panel.nextSibling);
    wiz.reviewCardEl = card;
  }

  function updateReviewCard() {
    if (!wiz.reviewCardEl) return;

    if (window.MILANO_refreshSummary) window.MILANO_refreshSummary();

    var items = document.querySelectorAll('.milano-summary__item');
    if (items.length === 0) return;

    var capValue = '', capPrice = '', laceValue = '', lengthValue = '';
    var capContainer = null;
    var allVisibleForCap = document.querySelectorAll('.milano-always-visible');
    for (var avi = 0; avi < allVisibleForCap.length; avi++) {
      var cavt = allVisibleForCap[avi].querySelector('.bold_option_title');
      var cavn = cavt ? (cavt.textContent || '').trim().toLowerCase() : '';
      if (cavn.indexOf('cap size') > -1 || cavn.indexOf('base size') > -1) {
        capContainer = allVisibleForCap[avi];
        break;
      }
    }
    if (capContainer) {
      var capRadio = capContainer.querySelector('input[type="radio"]:checked');
      if (capRadio) {
        var capWrap = capRadio.closest('.bold_option_value');
        var titleEl = capWrap ? capWrap.querySelector('.bold_option_value_title') : null;
        if (titleEl) {
          capValue = titleEl.textContent.replace(/\s+/g, ' ').trim();
        }
        // v2026-06-08: surface the cap-size upcharge on its review line (e.g.
        // "Cap: Small (+$100)") so it reads like the other priced services. Cap
        // Size is an always-visible SC Option handled separately from the wizard
        // summary items, so its price wasn't being shown. Prefers the sale price
        // if a DN strike pair is present.
        var capPriceEl = capWrap ? capWrap.querySelector('.bold_option_value_price') : null;
        if (capPriceEl) {
          var capSaleEl = capPriceEl.querySelector('.mw-opt-price-sale');
          var capPriceSrc = capSaleEl ? capSaleEl.textContent : capPriceEl.textContent;
          var capPriceNum = (capPriceSrc || '').replace(/\.00/g, '').replace(/[^\d.]/g, '');
          if (capPriceNum && parseFloat(capPriceNum) > 0) capPrice = '+$' + capPriceNum;
        }
      }
      if (!capValue) {
        var capSelect = capContainer.querySelector('select');
        if (capSelect && capSelect.selectedIndex > 0) capValue = capSelect.options[capSelect.selectedIndex].text.trim();
      }
    }

    var lengthChecked = document.querySelector(
      'fieldset.radio__fieldset--variant-option-image input[type="radio"]:checked'
    );
    if (lengthChecked) {
      var lengthBtn = lengthChecked.closest('.radio__button');
      var lengthTitle = lengthBtn ? lengthBtn.querySelector('.option-title') : null;
      lengthValue = lengthTitle ? lengthTitle.textContent.trim().replace(/\s+/g, ' ') : '';
    }

    var allChecked = document.querySelectorAll('.radio__buttons input[type="radio"]:checked');
    for (var ri = 0; ri < allChecked.length; ri++) {
      var rc = allChecked[ri];
      if (rc.closest('.radio__fieldset--variant-option-image')) continue;
      var rcBtn = rc.closest('.radio__button');
      var laceLbl = (rcBtn && rcBtn.querySelector('.option-title')) ||
                    (rc.parentElement && rc.parentElement.querySelector('.option-title'));
      if (laceLbl) { laceValue = laceLbl.textContent.trim(); break; }
    }

    var reviewTitle = wiz.isTopper ? 'YOUR TOPPER IS READY' : 'YOUR WIG IS READY';
    var html = '<div class="mw-review-card__title">' + reviewTitle + '</div>';
    html += '<div class="mw-review-card__items">';

    if (lengthValue) {
      html += '<div class="mw-review-card__line">Length: <strong>' + lengthValue + '</strong></div>';
      if (capValue) html += '<div class="mw-review-card__line">Cap: <strong>' + capValue + '</strong>' + (capPrice ? ' <span class="mw-review-card__price">(' + capPrice + ')</span>' : '') + '</div>';
      if (laceValue) html += '<div class="mw-review-card__line">Lace: <strong>' + laceValue + '</strong></div>';
    } else if (capValue || laceValue) {
      html += '<div class="mw-review-card__line">';
      if (capValue) html += 'Cap: <strong>' + capValue + '</strong>' + (capPrice ? ' <span class="mw-review-card__price">(' + capPrice + ')</span>' : '');
      if (capValue && laceValue) html += ' \u00B7 ';
      if (laceValue) html += 'Lace: <strong>' + laceValue + '</strong>';
      html += '</div>';
    }

    var virtualAsPictured = [];
    var hasCutSummary = false;
    for (var ci = 0; ci < items.length; ci++) {
      var cn = items[ci].querySelector('.milano-summary__item-name');
      if (cn && /cut/i.test(cn.textContent)) { hasCutSummary = true; break; }
    }
    if (wiz.virtualCutSelected && !hasCutSummary) virtualAsPictured.push('Cut');

    var hasColorSummary = false;
    for (var coi = 0; coi < items.length; coi++) {
      var con = items[coi].querySelector('.milano-summary__item-name');
      if (con && /color/i.test(con.textContent)) { hasColorSummary = true; break; }
    }
    if (wiz.virtualColorSelected && !hasColorSummary) virtualAsPictured.push('Color');

    var specificLines = [];
    var asPicturedKeys = [];
    var reassureColor = false, reassureCut = false;

    for (var i = 0; i < items.length; i++) {
      var nameEl = items[i].querySelector('.milano-summary__item-name');
      var priceEl = items[i].querySelector('.milano-summary__item-price');
      if (!nameEl) continue;

      var rawName = nameEl.textContent.trim();
      var price = priceEl ? priceEl.textContent.trim() : '';

      var cleanedName = rawName.replace(/\s*[\(\+]?\$[\d,.]+\s*(usd)?\)?/gi, '').trim();

      var short = cleanedName
        .replace(/^.*Secure.*Method:\s*/i, 'Secure: ')
        .replace(/^Add Color Service.*?:\s*/i, 'Color: ')
        .replace(/^Color Service[s]?.*:\s*/i, 'Color: ')
        .replace(/^Topper Color.*:\s*/i, 'Color: ')
        .replace(/^Personalize Your Cut:\s*/i, 'Cut: ')
        .replace(/^Milano Signature.*Cut:\s*/i, 'Cut: ')
        .replace(/^(?:Please )?Style My .+?:\s*/i, 'Style: ')
        .replace(/^Location of Part:\s*/i, 'Part: ')
        .replace(/^Position of Face Framing:\s*/i, 'Framing: ')
        .replace(/^Bangs or Fringe:\s*/i, 'Bangs: ')
        .replace(/^(?:Adjust|Select)(?: Ponytail)? Cap Size:\s*/i, 'Cap: ')
        .replace(/^Add Ear Pieces:\s*/i, 'Ear Pieces: ')
        .replace(/^Are You Blending.*:\s*/i, 'Hairline: ')
        .replace(/^Front Hairline:\s*/i, 'Hairline: ')
        .replace(/Most chosen/gi, '')
        .replace(/Recommended/gi, '')
        .replace(/Included/gi, '')
        .replace(/\s*\u2014\s.+$/g, '')
        .trim();

      var colonIdx = short.indexOf(': ');
      var labelPart = colonIdx > -1 ? short.substring(0, colonIdx) : '';
      var valPart = colonIdx > -1 ? short.substring(colonIdx + 2).trim() : '';

      var cleanPrice = price.replace(/\.00/g, '').replace(/\s*(usd)/gi, '').trim();
      if (cleanPrice === '+$0' || cleanPrice === '$0' || cleanPrice === '') cleanPrice = '';

      if (/^No\s+\w+$/i.test(valPart)) {
        if (labelPart) asPicturedKeys.push(labelPart);
        continue;
      }

      if (labelPart === 'Color' && !/^no\b/i.test(valPart)) reassureColor = true;
      else if (labelPart === 'Cut' && !/^no\b/i.test(valPart)) reassureCut = true;

      var includedSuffix = '';
      if (labelPart === 'Secure' && /clips\s*&?\s*combs/i.test(valPart) && !cleanPrice) {
        includedSuffix = ' <span class="mw-review-card__included">included</span>';
      }

      var lineHtml = '<div class="mw-review-card__line">';
      if (colonIdx > -1) {
        lineHtml += short.substring(0, colonIdx + 2) + '<strong>' + valPart + '</strong>' + includedSuffix;
      } else {
        lineHtml += short;
      }
      if (cleanPrice) lineHtml += ' <span class="mw-review-card__price">(' + cleanPrice + ')</span>';
      lineHtml += '</div>';
      specificLines.push(lineHtml);
    }

    for (var sl = 0; sl < specificLines.length; sl++) html += specificLines[sl];

    var combinedAsPictured = virtualAsPictured.concat(asPicturedKeys);
    if (combinedAsPictured.length > 0) {
      var seen = {};
      var uniqKeys = [];
      for (var ak = 0; ak < combinedAsPictured.length; ak++) {
        if (!seen[combinedAsPictured[ak]]) {
          seen[combinedAsPictured[ak]] = true;
          uniqKeys.push(combinedAsPictured[ak]);
        }
      }
      html += '<div class="mw-review-card__line mw-review-card__line--grouped">';
      html += uniqKeys.join(' \u00B7 ') + ': <strong>As pictured</strong>';
      html += '</div>';
    }

    html += '</div>';

    // Discount Ninja owns the BASE-product discount. Paid SC Options services are
    // added at full price so the review card matches the PDP/cart-bar base-only
    // promotion presentation. Fall back to MILANO_PDP_PRICE, then full price.
    var fw = document.querySelector('product-form[data-form-wrapper]');
    var serviceDollars = getServicesDollars();
    var mwMsrpCents = fw ? parseInt(fw.getAttribute('data-variant-price') || '0', 10) : 0;
    var mwBase = mwMsrpCents > 0 ? mwMsrpCents / 100 : 0;
    var mwMoney = function (n) { return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };

    var dnBaseCents = 0;
    var dnSaleCentsEl = document.querySelector('.limoniapps-discountninja-shortcode-discounted_price [data-limoniapps-discountninja-price-cents-basecurrency]');
    if (dnSaleCentsEl) {
      dnBaseCents = parseInt(dnSaleCentsEl.getAttribute('data-limoniapps-discountninja-price-cents-basecurrency'), 10) || 0;
    }
    if (dnBaseCents === 0) {
      var pdp = window.MILANO_PDP_PRICE;
      if (pdp && pdp.discountSource === 'discount-ninja-dom' && pdp.isDiscounted && pdp.priceCents > 0) {
        dnBaseCents = Math.max(0, pdp.priceCents - (pdp.selectedOptionsCents || 0));
      }
    }

    var mwOnSale = mwMsrpCents > 0 && dnBaseCents > 0 && dnBaseCents < mwMsrpCents;
    var ratio = mwOnSale ? (dnBaseCents / mwMsrpCents) : 1;   // discount ratio derived from the BASE
    var regularTotal = mwBase + serviceDollars;              // full price (base + add-ons)
    var wigShown = mwBase * ratio;                           // discounted base only
    var svcShown = serviceDollars;                           // services remain full price
    var discTotal = wigShown + svcShown;                     // base sale + full-price services

    // Itemization (2026-08-06): Options (setup surcharges) and Services shown
    // as separate lines; the Total math is unchanged (setup + services = the
    // same lump), so it still matches the cart exactly.
    var mwSetupDollars = getSetupOptionsDollars();
    if (mwSetupDollars > serviceDollars) mwSetupDollars = serviceDollars;
    var mwSvcOnlyDollars = serviceDollars - mwSetupDollars;

    html += '<div class="mw-review-card__total">';
    if (mwBase > 0) {
      var wigLabel = wiz.isTopper ? 'Topper' : 'Wig';
      html += '<div class="mw-review-card__line">' + wigLabel + ': <strong>$' + mwMoney(wigShown) + '</strong></div>';
      if (mwSetupDollars > 0) {
        getSetupOptionsBreakdown().forEach(function (su) {
          html += '<div class="mw-review-card__line">' + (su.name || 'Options') + ': <strong>+$' + mwMoney(su.dollars) + '</strong></div>';
        });
      }
      if (mwSvcOnlyDollars > 0) {
        html += '<div class="mw-review-card__line">Services: <strong>+$' + mwMoney(mwSvcOnlyDollars) + '</strong></div>';
      }
      if (mwOnSale) {
        html += '<div class="mw-review-card__total-line">Total: ' +
          '<del class="mw-review-card__was">$' + mwMoney(regularTotal) + '</del> ' +
          '<strong class="mw-review-card__now">$' + mwMoney(discTotal) + '</strong>' +
          '<span class="mw-review-card__saved">Sale price applied</span></div>';
      } else {
        html += '<div class="mw-review-card__total-line">Total: <strong>$' + mwMoney(discTotal) + '</strong></div>';
      }
    } else if (serviceDollars > 0) {
      if (mwSetupDollars > 0) {
        getSetupOptionsBreakdown().forEach(function (su) {
          html += (su.name || 'Options') + ': <strong>+$' + mwMoney(su.dollars) + '</strong> ';
        });
      }
      if (mwSvcOnlyDollars > 0) {
        html += 'Services: <strong>+$' + mwMoney(mwSvcOnlyDollars) + '</strong>';
      }
    }
    html += '</div>';

    // v1.6.42: simplified reminder — single generic message when any paid
    // services are added. Replaces per-step STEP_REASSURANCE config that
    // was over-engineered for the same outcome.
    // v2026-06-25: compact review-card reminder RESTORED (Kevin) — one tasteful
    // line reassuring the shopper that paid Color/Cut personalizations are
    // finalized via email after checkout, so the wizard's per-step promise isn't
    // forgotten at the summary. Shown ONLY when a real Color/Cut service is
    // selected; mirrors STEP_REASSURANCE intent, ~1 line of real estate.
    if (reassureColor || reassureCut) {
      var rWhat = (reassureColor && reassureCut) ? 'color &amp; cut'
                : reassureColor ? 'color' : 'cut';
      html += '<div class="mw-review-card__reminder">' +
        '<svg class="mw-review-card__reminder-ic" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false">' +
        '<path fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round" d="M1.6 3.7h12.8v8.6H1.6z"/>' +
        '<path fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" d="M2 4.2l6 4 6-4"/></svg>' +
        '<span>After checkout, our team emails you to perfect your <strong>' + rWhat + '</strong> — reference photos welcome.</span>' +
        '</div>';
    }

    html += '<div class="mw-review-card__actions">';
    // v1.6.42: <a href="#"> changed to <button> — eliminates the hash-jump
    // artifact where the click would scroll to top before goToStep ran.
    // CSS already styles .mw-review-card__edit as a text-link with explicit
    // border/background/appearance resets, so visual is identical.
    html += '<button type="button" class="mw-review-card__edit">Edit selections</button>';
    html += '</div>';

    wiz.reviewCardEl.innerHTML = html;

    var editLink = wiz.reviewCardEl.querySelector('.mw-review-card__edit');
    if (editLink) {
      editLink.addEventListener('click', function(e) {
        e.preventDefault();
        goToStep(0);
      });
    }
  }

  // =========================================================================
  // ENHANCE FORM
  // =========================================================================

  function enhanceFormElements() {
    injectLaceSubtitles();
    titleCaseLaceLabels();
    applyLabelRenames();
    injectBadgesAndLabels();
    injectInlineHelp();
    injectAddonHeaders();
    reorderColorOptions();
  }

  // =========================================================================
  // LACE SUBTITLES
  // =========================================================================

  function injectLaceSubtitles() {
    // Same signal LACE_POPUP_MAP uses to pick the ponytail lace popup: pathname
    // + product title. Every product in the ponytail collection carries
    // "ponytail" in both, and nothing else customer-facing does.
    var ponytailSubs = /ponytail/i.test(getCollectionSignal({ rows: [] })) ? LACE_SUBS_PONYTAIL : null;
    var radioButtons = document.querySelectorAll('.radio__buttons .radio__button');
    for (var rb = 0; rb < radioButtons.length; rb++) {
      var labelEl = radioButtons[rb].querySelector('label.radio__label');
      if (!labelEl) continue;
      var titleSpan = labelEl.querySelector('.option-title');
      var raw = titleSpan ? titleSpan.textContent : labelEl.textContent;
      // Collapse internal whitespace the same way titleCaseLaceLabels does —
      // one SC label ships as "Zig Zag Standard  Lace" (double space) and would
      // otherwise miss its key on the exact-match lookup below.
      var text = raw.replace(/\s+/g, ' ').trim().toLowerCase();
      var sub = (ponytailSubs && ponytailSubs[text]) || LACE_SUBS[text];
      if (!sub) continue;

      // Equal-height boxes. The .radio__button wrappers already stretch to the
      // tallest sibling, but the label inside sizes to its own content, so a
      // two- or three-line description leaves the other box visibly short.
      // Marked here rather than in CSS because only this loop knows which
      // groups carry subtitles — same pattern as .mw-length-grid. No-op where
      // both descriptions are a single line.
      var laceGroup = radioButtons[rb].closest('.radio__buttons');
      if (laceGroup) laceGroup.classList.add('mw-lace-equal');

      if (!labelEl.querySelector('.mw-lace-sub')) {
        var el = document.createElement('span');
        el.className = 'mw-lace-sub';
        el.textContent = sub;
        labelEl.appendChild(el);
      }
    }
  }

  // =========================================================================
  // LACE LABEL CASE — PREVIEW ONLY
  // =========================================================================

  function titleCaseLaceLabels() {
    var titles = document.querySelectorAll(
      '.radio__buttons .radio__button .option-title'
    );

    for (var ti = 0; ti < titles.length; ti++) {
      var el = titles[ti];
      if (el.getAttribute('data-mw-cased') === '1') continue;

      // Length uses its own number/name split and intentionally keeps the name
      // compact. Do not title-case or restyle those labels from this pass.
      if (el.closest('.mw-length-grid, .mw-length-fieldset, .radio__fieldset--variant-option-image')) continue;

      var raw = (el.textContent || '').trim();
      if (!raw) continue;

      var normalized = raw.toLowerCase().replace(/\s+/g, ' ').trim();
      var isLaceLabel = !!LACE_SUBS[normalized] || /\bzig\s+zag\b/i.test(raw) || /\blace\b/i.test(raw);

      var letters = raw.replace(/[^A-Za-z]/g, '');
      if (!letters) continue;

      var upperCount = letters.replace(/[^A-Z]/g, '').length;
      if (!isLaceLabel && upperCount / letters.length < 0.7) continue;

      var cased = raw.toLowerCase().replace(/\b([a-z])([a-z]*)/g, function(_, h, t) {
        return h.toUpperCase() + t;
      });

      el.textContent = cased;
      el.setAttribute('data-mw-cased', '1');
      if (isLaceLabel) el.setAttribute('data-mw-lace-title-case', '1');
    }
  }

  // =========================================================================
  // LABEL RENAMES
  // =========================================================================

  function applyLabelRenames() {
    var vals = document.querySelectorAll('.milano-option-row__body .bold_option_value');
    for (var i = 0; i < vals.length; i++) {
      var lbl = vals[i].querySelector('label');
      if (!lbl || lbl.getAttribute('data-mw-renamed')) continue;
      var key = stripPriceText(lbl.textContent);
      if (!LABEL_RENAMES[key]) continue;

      // v2026-06-05: The shared "As is" → "No Cut — keep as pictured" rename is
      // correct on the Cut step but reads as nonsense on the Style step (the
      // Style + Part Location groups). Scope a cleaner "Keep As Pictured" label
      // to those groups only; the Cut step keeps "No Cut — keep as pictured".
      var renameTo = LABEL_RENAMES[key];
      if (key === 'as is') {
        var ownerRow = vals[i].closest('.milano-option-row');
        var ownerName = ownerRow ? (ownerRow.getAttribute('data-option-name') || '') : '';
        if (!ownerName) {
          var ownerBold = vals[i].closest('.bold_option');
          if (ownerBold) {
            var ownerTitle = ownerBold.querySelector('.bold_option_title');
            ownerName = ownerTitle ? ownerTitle.textContent : (ownerBold.getAttribute('data-option-name') || '');
          }
        }
        ownerName = ownerName.toLowerCase();
        if (ownerName.indexOf('style') > -1 || ownerName.indexOf('part') > -1) {
          renameTo = 'Keep As Pictured';
        }
      }

      var renamed = false;

      for (var n = 0; n < lbl.childNodes.length; n++) {
        if (lbl.childNodes[n].nodeType === 3) {
          var orig = lbl.childNodes[n].textContent;
          if (orig.trim().toLowerCase().indexOf(key) > -1) {
            lbl.childNodes[n].textContent = orig.replace(
              new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'),
              renameTo
            );
            renamed = true;
            break;
          }
        }
      }

      if (!renamed) {
        var spans = lbl.querySelectorAll('span:not(.bold_option_value_price):not(.mw-badge):not(.mw-no-color-sub):not(.mw-included)');
        for (var s = 0; s < spans.length; s++) {
          if (spans[s].textContent.trim().toLowerCase() === key) {
            spans[s].textContent = renameTo;
            renamed = true;
            break;
          }
        }
      }

      if (renamed) lbl.setAttribute('data-mw-renamed', '1');
    }
  }

  // =========================================================================
  // BADGES AND LABELS
  // =========================================================================

  function injectBadgesAndLabels() {
    var vals = document.querySelectorAll('.milano-option-row__body .bold_option_value');
    for (var i = 0; i < vals.length; i++) {
      var lbl = vals[i].querySelector('label');
      if (!lbl) continue;
      var text = stripPriceText(lbl.textContent);

      if (text.indexOf('clips') > -1 &&
          (text.indexOf('wigrip') > -1 || text.indexOf('wi grip') > -1 || text.indexOf('wig grip') > -1 ||
           text.indexOf('topgrip') > -1 || text.indexOf('top grip') > -1 || text.indexOf('top-grip') > -1) &&
          !vals[i].querySelector('.mw-badge')) {
        var badge = document.createElement('span');
        badge.className = 'mw-badge';
        badge.textContent = 'Recommended';
        lbl.appendChild(badge);
      }

      if (text.indexOf('no color service') > -1 || text.indexOf('keep original color') > -1 || (text.indexOf('no color') > -1 && text.indexOf('keep as pictured') > -1)) {
        vals[i].classList.add('mw-no-color');
        if (!vals[i].nextElementSibling || !vals[i].nextElementSibling.classList.contains('mw-color-sep')) {
          var sep = document.createElement('div');
          sep.className = 'mw-color-sep';
          sep.textContent = 'Select one or more';
          vals[i].parentNode.insertBefore(sep, vals[i].nextSibling);
        }
      }

      if ((text.indexOf('as is') > -1 || text.indexOf('as-pictured') > -1 || text.indexOf('no cut') > -1) && !vals[i].classList.contains('mw-no-cut')) {
        var cutRow = vals[i].closest('.milano-option-row');
        var cutName = cutRow ? (cutRow.getAttribute('data-option-name') || '').toLowerCase() : '';
        if (cutName.indexOf('cut') > -1 || cutName.indexOf('signature') > -1) {
          vals[i].classList.add('mw-no-cut');
          var cutParent = vals[i].parentElement;
          if (cutParent && cutParent.firstChild !== vals[i]) {
            cutParent.insertBefore(vals[i], cutParent.firstChild);
          }
          if (!vals[i].nextElementSibling || !vals[i].nextElementSibling.classList.contains('mw-cut-sep')) {
            var cutSep = document.createElement('div');
            cutSep.className = 'mw-cut-sep';
            cutSep.textContent = 'Choose a cut style:';
            vals[i].parentNode.insertBefore(cutSep, vals[i].nextSibling);
          }
        }
      }

      if ((text.indexOf('as is') > -1 || text.indexOf('as-pictured') > -1) &&
          !vals[i].classList.contains('mw-no-style') &&
          !vals[i].classList.contains('mw-no-cut')) {
        var styleSkipRow = vals[i].closest('.milano-option-row');
        var srowName = styleSkipRow ? (styleSkipRow.getAttribute('data-option-name') || '').toLowerCase() : '';
        if (srowName.indexOf('style') > -1) {
          vals[i].classList.add('mw-no-style');
          var styleSkipParent = vals[i].parentElement;
          if (styleSkipParent && styleSkipParent.firstChild !== vals[i]) {
            styleSkipParent.insertBefore(vals[i], styleSkipParent.firstChild);
          }
          if (!vals[i].nextElementSibling || !vals[i].nextElementSibling.classList.contains('mw-style-sep')) {
            var styleSep = document.createElement('div');
            styleSep.className = 'mw-style-sep';
            styleSep.textContent = 'Or choose a style:';
            vals[i].parentNode.insertBefore(styleSep, vals[i].nextSibling);
          }
        }

      } else if (!vals[i].getAttribute('data-mw-ear')) {
        var earRow = vals[i].closest('.milano-option-row');
        var earName = earRow ? (earRow.getAttribute('data-option-name') || '').toLowerCase() : '';
        if (earName.indexOf('ear') > -1) {
          vals[i].setAttribute('data-mw-ear', '1');

          if (text === 'no' || (text.indexOf('no') === 0 && text.length < 30)) {
            vals[i].classList.add('mw-no-ear');
            var earParent = vals[i].parentElement;
            if (earParent && earParent.firstChild !== vals[i]) {
              earParent.insertBefore(vals[i], earParent.firstChild);
            }
            if (!vals[i].querySelector('.mw-badge')) {
              var recBadge = document.createElement('span');
              recBadge.className = 'mw-badge';
              recBadge.textContent = 'Recommended';
              lbl.appendChild(recBadge);
            }
          }

          if (text.indexOf('yes') > -1 && text.indexOf('no') === -1 && !vals[i].querySelector('.mw-opt-inline-desc')) {
            for (var en = 0; en < lbl.childNodes.length; en++) {
              if (lbl.childNodes[en].nodeType === 3) {
                var earOrig = lbl.childNodes[en].textContent;
                if (earOrig.toLowerCase().indexOf('suggested') > -1 || earOrig.toLowerCase().indexOf('if no') > -1) {
                  lbl.childNodes[en].textContent = earOrig.replace(/\s*[-\u2013\u2014]\s*(?:Suggested|If no).*/i, '').trim();
                  break;
                }
              }
            }
            var earInline = document.createElement('span');
            earInline.className = 'mw-opt-inline-desc';
            earInline.textContent = ' \u2014 For no bio hair by ears';
            lbl.appendChild(earInline);
          }
        }

      } else if (!vals[i].getAttribute('data-mw-hairline')) {
        var hlRow = vals[i].closest('.milano-option-row');
        var hlName = hlRow ? (hlRow.getAttribute('data-option-name') || '').toLowerCase() : '';
        if (hlName.indexOf('hairline') > -1 || hlName.indexOf('blending') > -1) {
          vals[i].setAttribute('data-mw-hairline', '1');

          if (text === 'yes' && !vals[i].querySelector('.mw-opt-inline-desc')) {
            if (!vals[i].querySelector('.mw-badge')) {
              var hlBadge = document.createElement('span');
              hlBadge.className = 'mw-badge';
              hlBadge.textContent = 'Recommended';
              lbl.appendChild(hlBadge);
            }
            var hlYesInline = document.createElement('span');
            hlYesInline.className = 'mw-opt-inline-desc';
            hlYesInline.textContent = ' \u2014 I have bio hair to blend';
            lbl.appendChild(hlYesInline);
          } else if (text === 'no' && !vals[i].querySelector('.mw-opt-inline-desc')) {
            var hlNoInline = document.createElement('span');
            hlNoInline.className = 'mw-opt-inline-desc';
            hlNoInline.textContent = ' \u2014 My hairline is thinning';
            lbl.appendChild(hlNoInline);
          } else if (text.indexOf('undecided') > -1 && !vals[i].querySelector('.mw-opt-inline-desc')) {
            for (var hn = 0; hn < lbl.childNodes.length; hn++) {
              if (lbl.childNodes[hn].nodeType === 3) {
                var hlOrig = lbl.childNodes[hn].textContent;
                if (hlOrig.toLowerCase().indexOf('will contact') > -1 || hlOrig.toLowerCase().indexOf('we will') > -1 || hlOrig.toLowerCase().indexOf('or both') > -1) {
                  lbl.childNodes[hn].textContent = hlOrig.replace(/\s*[-\u2013\u2014]\s*.*/i, '').trim();
                  break;
                }
              }
            }
            var hlUndInline = document.createElement('span');
            hlUndInline.className = 'mw-opt-inline-desc';
            hlUndInline.textContent = ' \u2014 We\u2019ll reach out to help';
            lbl.appendChild(hlUndInline);
          }
        }
      }
    }

    var allRows = document.querySelectorAll('.milano-option-row');
    for (var sg = 0; sg < allRows.length; sg++) {
      var rowName = (allRows[sg].getAttribute('data-option-name') || '').toLowerCase();
      if (rowName.indexOf('position of') > -1 || rowName.indexOf('bangs or fringe') > -1) {
        allRows[sg].classList.add('mw-subgate');
        var body = allRows[sg].querySelector('.milano-option-row__body');
        if (body && !body.querySelector('.mw-subgate-label')) {
          var label = document.createElement('div');
          label.className = 'mw-subgate-label';
          if (rowName.indexOf('position') > -1) label.textContent = 'Where should framing end?';
          if (rowName.indexOf('bangs') > -1) label.textContent = 'What bang style?';
          body.insertBefore(label, body.firstChild);
        }
      }
      if (rowName.indexOf('style my') > -1 || rowName.indexOf('please style') > -1) {
        var styleBody = allRows[sg].querySelector('.milano-option-row__body');
        if (styleBody && !styleBody.querySelector('.mw-section-title')) {
          var stTitle = document.createElement('div');
          stTitle.className = 'mw-section-title';
          stTitle.textContent = 'Style';
          styleBody.insertBefore(stTitle, styleBody.firstChild);
        }
      }
      if (rowName.indexOf('location of part') > -1) {
        var partBody = allRows[sg].querySelector('.milano-option-row__body');
        if (partBody && !partBody.querySelector('.mw-section-title')) {
          var ptTitle = document.createElement('div');
          ptTitle.className = 'mw-section-title';
          ptTitle.textContent = 'Part Location';
          partBody.insertBefore(ptTitle, partBody.firstChild);
        }
      }
    }

    var prices = document.querySelectorAll('.milano-option-row__body .bold_option_value_price');
    for (var p = 0; p < prices.length; p++) {
      var pt = (prices[p].textContent || '').trim();
      if ((pt === '$0.00' || pt === '$0.00 USD' || pt === '$0') && !prices[p].querySelector('.mw-included')) {
        prices[p].innerHTML = '<span class="mw-included">Included</span>';
      }
    }
  }

  // =========================================================================
  // INLINE HELP LINKS
  // =========================================================================

  function injectInlineHelp() {
    var allRows = document.querySelectorAll('.milano-option-row');
    for (var i = 0; i < allRows.length; i++) {
      var rowName = (allRows[i].getAttribute('data-option-name') || '').toLowerCase();

      for (var h = 0; h < INLINE_HELP_MAP.length; h++) {
        if (rowName.indexOf(INLINE_HELP_MAP[h].nameMatch) === -1) continue;

        var popupId = INLINE_HELP_MAP[h].popup;
        if (!document.getElementById(popupId)) continue;

        var labelEl = allRows[i].querySelector('.mw-subgate-label') ||
                      allRows[i].querySelector('.mw-section-title');
        if (!labelEl || labelEl.querySelector('.mw-inline-help')) continue;

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mw-inline-help';
        btn.setAttribute('data-popup-id', popupId);
        btn.innerHTML =
          '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
            '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
            '<text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="10" font-weight="700" font-family="inherit">?</text>' +
          '</svg>' +
          '<span>' + INLINE_HELP_MAP[h].label + '</span>';
        btn.addEventListener('click', function(e) {
          e.preventDefault();
          var pid = this.getAttribute('data-popup-id');
          if (pid && window.showSecurePopup) {
            window.showSecurePopup(pid);
          } else if (pid) {
            var container = document.getElementById(pid);
            if (container) {
              container.style.display = 'flex';
              document.body.classList.add('mw-popup-open');
            }
          }
        });
        labelEl.appendChild(btn);
        break;
      }
    }
  }

  // v1.6.42: updateStepNotice function removed. STEP_REASSURANCE per-step
  // notice replaced by a single generic reminder line in the review card,
  // emitted directly from updateReviewCard when getServicesDollars() > 0.

  // =========================================================================
  // ADDON HEADERS
  // =========================================================================

  function injectAddonHeaders() {
    var allRows = document.querySelectorAll('.milano-option-row');
    for (var i = 0; i < allRows.length; i++) {
      if (allRows[i].getAttribute('data-mw-addon-header')) continue;
      var rowName = (allRows[i].getAttribute('data-option-name') || '').toLowerCase();

      for (var a = 0; a < ADDON_HEADERS.length; a++) {
        var cfg = ADDON_HEADERS[a];
        var matches = rowName.indexOf(cfg.nameMatch) > -1;
        if (!matches && cfg.altMatch) matches = rowName.indexOf(cfg.altMatch) > -1;
        if (!matches) continue;

        // The step heading already carries this exact title/description/help
        // when Ear Pieces is the only Secure option; avoid repeating it above
        // the values. Mixed Secure stages keep the existing in-row header.
        if (cfg.nameMatch === 'ear piece' && isEarPiecesOnlySecureRow(allRows[i])) break;

        var body = allRows[i].querySelector('.milano-option-row__body');
        if (!body) continue;

        allRows[i].setAttribute('data-mw-addon-header', '1');

        var header = document.createElement('div');
        header.className = 'mw-addon-header' + (cfg.warm ? ' mw-addon-header--warm' : '');

        var titleEl = document.createElement('div');
        titleEl.className = 'mw-addon-title';
        titleEl.textContent = cfg.title;

        if (cfg.helpId && document.getElementById(cfg.helpId)) {
          var helpBtn = document.createElement('button');
          helpBtn.type = 'button';
          helpBtn.className = 'mw-inline-help';
          helpBtn.setAttribute('data-popup-id', cfg.helpId);
          helpBtn.innerHTML =
            '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
              '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
              '<text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="10" font-weight="700" font-family="inherit">?</text>' +
            '</svg>' +
            '<span>' + (cfg.helpText || 'Learn more') + '</span>';
          helpBtn.addEventListener('click', function(e) {
            e.preventDefault();
            var pid = this.getAttribute('data-popup-id');
            if (pid && window.showSecurePopup) {
              window.showSecurePopup(pid);
            } else if (pid) {
              var container = document.getElementById(pid);
              if (container) {
                container.style.display = 'flex';
                document.body.classList.add('mw-popup-open');
              }
            }
          });
          titleEl.appendChild(helpBtn);
        }

        header.appendChild(titleEl);

        if (cfg.desc) {
          var descEl = document.createElement('div');
          descEl.className = 'mw-addon-desc';
          descEl.textContent = cfg.desc;
          header.appendChild(descEl);
        }

        body.insertBefore(header, body.firstChild);
        break;
      }
    }
  }

  // =========================================================================
  // REORDER COLOR OPTIONS
  // =========================================================================

  var COLOR_SORT_ORDER = [
    'partial rooting',
    'full rooting',
    'mini highlight',
    'money piece',
    'highlight or lowlight',
    'lowlight',
    'toning',
    'reverse balayage',
    'balayage',
    'deux balayage',
    'custom color'
  ];

  function reorderColorOptions() {
    var colorRows = [];
    for (var i = 0; i < wiz.steps.length; i++) {
      if (wiz.steps[i].id !== 1) continue;
      colorRows = wiz.steps[i].rows;
      break;
    }
    if (colorRows.length === 0) return;

    for (var r = 0; r < colorRows.length; r++) {
      var body = colorRows[r].querySelector('.milano-option-row__body');
      if (!body) continue;
      if (body.getAttribute('data-mw-color-sorted') === '1') continue;

      var checkboxVals = body.querySelectorAll('.bold_option_value:not(.mw-no-color)');
      if (checkboxVals.length < 3) continue;

      var items = [];
      for (var v = 0; v < checkboxVals.length; v++) {
        var lbl = checkboxVals[v].querySelector('label');
        var labelText = lbl ? stripPriceText(lbl.textContent) : '';
        var sortIdx = COLOR_SORT_ORDER.length;
        var bestLen = 0;

        for (var s = 0; s < COLOR_SORT_ORDER.length; s++) {
          if (labelText.indexOf(COLOR_SORT_ORDER[s]) > -1 && COLOR_SORT_ORDER[s].length > bestLen) {
            sortIdx = s;
            bestLen = COLOR_SORT_ORDER[s].length;
          }
        }
        items.push({ el: checkboxVals[v], idx: sortIdx });
      }

      items.sort(function(a, b) { return a.idx - b.idx; });

      var sep = body.querySelector('.mw-color-sep');
      var insertAfter = sep || body.querySelector('.mw-no-color');

      for (var d = 0; d < items.length; d++) {
        if (insertAfter) {
          insertAfter.parentNode.insertBefore(items[d].el, insertAfter.nextSibling);
          insertAfter = items[d].el;
        } else {
          body.appendChild(items[d].el);
        }
      }

      body.setAttribute('data-mw-color-sorted', '1');
      log('[Milano Wizard] Color options reordered (' + items.length + ' items)');
    }
  }

  // =========================================================================
  // REORDER CAP / LACE
  // =========================================================================

  function isNodeDisplayHidden(node) {
    var el = node;
    while (el && el.nodeType === 1 && el !== document.body) {
      if (el.style && el.style.display === 'none') return true;
      if (window.getComputedStyle) {
        var cs = window.getComputedStyle(el);
        if (cs && cs.display === 'none') return true;
      }
      el = el.parentElement;
    }
    return false;
  }

  function reorderForGrace() {
    if (!document.body.classList.contains('milano-grace')) return false;
    markGraceLengthGrid();

    // v1.6.43: querySelectorAll instead of querySelector to detect mid-render
    // duplicate state. When the theme / Globo re-renders variant fieldsets
    // on variant click, the new fieldset can momentarily co-exist with the
    // old one. Acting during that window has been causing the "duplicate
    // Length flash" Kevin reported. Defer until only one remains; the next
    // mutation cycle will reorder correctly.
    var lengthFieldsets = document.querySelectorAll('fieldset.radio__fieldset--variant-option-image');
    if (lengthFieldsets.length === 0) return false;

    // Palo Alto's HTMLUpdateUtility double-buffers variant-selects: the new
    // fieldset is inserted while the old fieldset is display:none for ~500ms.
    // Pick the live/visible Length fieldset immediately so Cap Size is rescued
    // from the old hidden variant-selects before that old node is removed.
    var length = null;
    for (var lf = 0; lf < lengthFieldsets.length; lf++) {
      if (!isNodeDisplayHidden(lengthFieldsets[lf])) {
        length = lengthFieldsets[lf];
        break;
      }
    }
    if (!length) length = lengthFieldsets[0];

    var cap = null;
    var allVisible = document.querySelectorAll('.milano-always-visible');
    for (var i = 0; i < allVisible.length; i++) {
      var t = allVisible[i].querySelector('.bold_option_title');
      var name = t ? (t.textContent || '').trim().toLowerCase() : '';
      if (name.indexOf('cap size') > -1 || name.indexOf('base size') > -1) {
        cap = allVisible[i];
        break;
      }
    }
    if (!cap) return false;

    var variantSelects = length.closest('variant-selects') || length.closest('.product__selectors');
    if (!variantSelects) return false;

    var lengthBranch = length;
    while (lengthBranch && lengthBranch.parentNode !== variantSelects) {
      lengthBranch = lengthBranch.parentNode;
    }
    if (!lengthBranch) return false;

    // v1.6.43: viewport branching removed. Order is now Length \u2192 Cap \u2192 Lace
    // on every viewport (was Cap \u2192 Length on desktop in v1.6.41). Per
    // 2026-05-26 Kevin: desktop should match mobile.
    var targetBefore = lengthBranch.nextElementSibling || null;

    // Idempotent: if cap is already directly after lengthBranch, no-op.
    if (cap.parentNode === variantSelects && cap.previousElementSibling === lengthBranch) {
      return true;
    }

    try {
      if (targetBefore && targetBefore.parentNode === variantSelects) {
        variantSelects.insertBefore(cap, targetBefore);
      } else {
        variantSelects.appendChild(cap);
      }
    } catch (_) {
      try { variantSelects.appendChild(cap); } catch (__) { return false; }
    }
    return true;
  }

  function scheduleGraceReorder() {
    if (!document.body.classList.contains('milano-grace')) return;
    var delays = [0, 40, 120, 260, 520, 760];
    for (var i = 0; i < delays.length; i++) {
      setTimeout(function() {
        reorderForGrace();
      }, delays[i]);
    }
  }

  function reorderCapAndLace() {
    var capEl = null;
    var allVisible = document.querySelectorAll('.milano-always-visible');
    for (var i = 0; i < allVisible.length; i++) {
      var t = allVisible[i].querySelector('.bold_option_title');
      var name = t ? (t.textContent || '').trim().toLowerCase() : '';
      if (!name) name = (allVisible[i].getAttribute('data-option-name') || '').toLowerCase();
      if (name.indexOf('cap size') > -1 || name.indexOf('base size') > -1) { capEl = allVisible[i]; break; }
    }

    var laceWrapper = null, laceLabel = null;
    var labels = document.querySelectorAll('.radio__legend__label.label-typography, .select__label.label-typography');
    for (var j = 0; j < labels.length; j++) {
      var lt = (labels[j].textContent || '').trim().toLowerCase();
      if (lt.indexOf('lace front') > -1 || lt.indexOf('deep lace') > -1) {
        laceLabel = labels[j];
        laceWrapper = walkUp(labels[j], 'selector-wrapper');
        break;
      }
    }

    if (!capEl || !laceWrapper) return;

    var variantSelects = document.querySelector('variant-selects, .product__selectors');
    var insertTarget = variantSelects || laceWrapper;

    // If Cap Size is already inside variant-selects, leave placement to
    // reorderForGrace(). Pulling a descendant out before variant-selects is
    // what made Cap jump above Length after native Length/Lace changes.
    if (insertTarget.contains && insertTarget.contains(capEl)) {
      // no-op
    } else if (!(capEl.compareDocumentPosition(insertTarget) & Node.DOCUMENT_POSITION_FOLLOWING)) {
      if (insertTarget.parentNode) {
        try {
          insertTarget.parentNode.insertBefore(capEl, insertTarget);
        } catch (_) {
          try { insertTarget.parentNode.appendChild(capEl); } catch (__) {}
        }
      }
    }

    var capTitle = capEl.querySelector('.bold_option_title');
    if (capTitle) {
      for (var c = 0; c < capTitle.childNodes.length; c++) {
        if (capTitle.childNodes[c].nodeType === 3) {
          var ct = capTitle.childNodes[c].textContent.trim();
          if (ct && ct.trim() !== 'Cap Size') {
            capTitle.childNodes[c].textContent = 'Cap Size';
          }
          break;
        }
      }
      capTitle.innerHTML = capTitle.innerHTML.replace(/\*/g, '');

      if (!capTitle.querySelector('.mw-cap-label')) {
        for (var cw = 0; cw < capTitle.childNodes.length; cw++) {
          var node = capTitle.childNodes[cw];
          if (node.nodeType === 3 && node.textContent.trim()) {
            var labelSpan = document.createElement('span');
            labelSpan.className = 'mw-cap-label';
            labelSpan.textContent = node.textContent.trim();
            node.parentNode.replaceChild(labelSpan, node);
            break;
          }
        }
      }

      if (!capTitle.querySelector('.mw-size-guide-native') && !capTitle.querySelector('.product__popup-toggle')) {
        var nativeBlock = document.querySelector('.product__form__size-chart');
        if (nativeBlock) {
          var nativeTrigger = nativeBlock.querySelector('button, a');
          if (nativeTrigger) {
            nativeTrigger.classList.add('mw-size-guide-native');
            capTitle.appendChild(nativeTrigger);
            nativeBlock.classList.add('mw-popup-relocated');
          }
        }
      }
    }

    if (laceLabel) {
      for (var l = 0; l < laceLabel.childNodes.length; l++) {
        if (laceLabel.childNodes[l].nodeType === 3) {
          var llt = laceLabel.childNodes[l].textContent.trim();
          if (llt && llt.trim() !== 'Lace Front') {
            laceLabel.childNodes[l].textContent = 'Lace Front';
          }
          break;
        }
      }

      if (!laceLabel.querySelector('.mw-lace-help')) {
        var laceHelpId = 'lace-compare';
        var laceSignal = getCollectionSignal({ rows: [] });
        for (var lm = 0; lm < LACE_POPUP_MAP.length; lm++) {
          if (LACE_POPUP_MAP[lm].pattern.test(laceSignal) && document.getElementById(LACE_POPUP_MAP[lm].popup)) {
            laceHelpId = LACE_POPUP_MAP[lm].popup;
            break;
          }
        }
        if (laceHelpId === 'lace-compare' && !document.getElementById('lace-compare')) {
          laceHelpId = null;
        }

        if (laceHelpId) {
          var laceHelp = document.createElement('button');
          laceHelp.type = 'button';
          laceHelp.className = 'mw-lace-help';
          laceHelp.setAttribute('data-popup-id', laceHelpId);
          laceHelp.innerHTML =
            '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
              '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
              '<text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="10" font-weight="700" font-family="inherit">?</text>' +
            '</svg>' +
            '<span>Compare</span>';
          laceHelp.addEventListener('click', function(e) {
            e.preventDefault();
            var pid = this.getAttribute('data-popup-id');
            if (pid && window.showSecurePopup) {
              window.showSecurePopup(pid);
            } else if (pid) {
              var container = document.getElementById(pid);
              if (container) {
                container.style.display = 'flex';
                document.body.classList.add('mw-popup-open');
              }
            }
          });
          laceLabel.appendChild(laceHelp);
        }
      }
    }
  }

  // =========================================================================
  // GRACE COLLECTION — Help links on variant selectors
  // =========================================================================

  function findGraceLengthGroup() {
    if (!document.body.classList.contains('milano-grace')) return null;

    var checked = document.querySelector('input[data-single-option-selector][name="options[Length]"]:checked');
    var any = checked || document.querySelector('input[data-single-option-selector][name="options[Length]"]');
    if (any) {
      var group = any.closest('.radio__buttons');
      if (group) return group;
    }

    var fieldsets = document.querySelectorAll('fieldset.radio__fieldset--variant-option-image, fieldset[data-option-position]');
    for (var i = 0; i < fieldsets.length; i++) {
      var text = ((fieldsets[i].querySelector('.radio__legend__label, legend') || {}).textContent || '').toLowerCase();
      if (text.indexOf('length') !== -1) return fieldsets[i].querySelector('.radio__buttons');
    }

    return null;
  }

  function markGraceLengthGrid() {
    if (!document.body.classList.contains('milano-grace')) return null;

    var lengthGroup = findGraceLengthGroup();
    var stale = document.querySelectorAll('fieldset.mw-length-fieldset');
    for (var s = 0; s < stale.length; s++) {
      if (!lengthGroup || stale[s] !== lengthGroup.closest('fieldset')) {
        stale[s].classList.remove('mw-length-fieldset');
        var staleGroup = stale[s].querySelector('.radio__buttons.mw-length-grid');
        if (staleGroup) staleGroup.classList.remove('mw-length-grid');
      }
    }

    if (!lengthGroup) return null;

    // Length option order is now owned by Shopify admin for first-paint stability.
    // Do not visually sort/move Length radio nodes in JS; moving native variant inputs
    // during Palo Alto renders can create unnecessary lifecycle risk.
    lengthGroup.classList.add('mw-length-grid');

    var count = lengthGroup.querySelectorAll('.radio__button').length;
    lengthGroup.setAttribute('data-mw-length-count', String(count));
    lengthGroup.classList.toggle('mw-length-many', count >= 7);
    lengthGroup.classList.toggle('mw-length-even', count > 0 && count % 2 === 0);
    lengthGroup.classList.toggle('mw-length-odd', count > 0 && count % 2 === 1);

    var fs = lengthGroup.closest('fieldset');
    if (fs) {
      fs.classList.add('mw-length-fieldset');
      fs.setAttribute('data-mw-length-count', String(count));
      fs.classList.toggle('mw-length-many', count >= 7);
      fs.classList.toggle('mw-length-even', count > 0 && count % 2 === 0);
      fs.classList.toggle('mw-length-odd', count > 0 && count % 2 === 1);
    }

    return lengthGroup;
  }

  function addGraceHelpLinks() {
    if (!document.body.classList.contains('milano-grace')) return;

    markGraceLengthGrid();
    var fieldsets = document.querySelectorAll('fieldset.radio__fieldset--variant-option-image, fieldset.mw-length-fieldset');

    for (var i = 0; i < fieldsets.length; i++) {
      var fs = fieldsets[i];
      if (fs.querySelector('.mw-lace-help')) continue;

      var legendLabel = fs.querySelector('.radio__legend__label');
      if (!legendLabel) continue;

      var text = (legendLabel.textContent || '').trim().toLowerCase();
      if (text.indexOf('length') === -1) continue;

      var popupId = 'grace-length';

      legendLabel.style.display = 'flex';
      legendLabel.style.alignItems = 'center';
      legendLabel.style.width = '100%';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mw-lace-help';
      btn.setAttribute('data-popup-id', popupId);
      btn.setAttribute('aria-label', 'View length & cap details');
      btn.innerHTML =
        '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
          '<circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/>' +
          '<text x="8" y="11.5" text-anchor="middle" fill="currentColor" font-size="10" font-weight="700" font-family="inherit">?</text>' +
        '</svg>' +
        '<span>View details</span>';
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        var pid = this.getAttribute('data-popup-id');
        if (pid && window.showSecurePopup) {
          window.showSecurePopup(pid);
        } else if (pid) {
          var container = document.getElementById(pid);
          if (container) {
            container.style.display = 'flex';
            document.body.classList.add('mw-popup-open');
          }
        }
      });
      legendLabel.appendChild(btn);
    }
  }

  function bootstrapGraceHelpLinks() {
    cleanupDuplicateGraceHelp();
    addGraceHelpLinks();
    if (!document.body.classList.contains('milano-grace')) return;
    var fn = function() {
      cleanupDuplicateGraceHelp();
      addGraceHelpLinks();
      return false;
    };
    if (window.MILANO_addBootstrap) {
      window.MILANO_addBootstrap(fn, false);
    } else {
      var attempts = 0;
      var interval = setInterval(function() {
        attempts++;
        fn();
        if (attempts >= 10) clearInterval(interval);
      }, 200);
    }
  }

  function cleanupDuplicateGraceHelp() {
    if (!document.body.classList.contains('milano-grace')) return;
    markGraceLengthGrid();
    var fieldsets = document.querySelectorAll('fieldset.radio__fieldset--variant-option-image, fieldset.mw-length-fieldset');
    for (var i = 0; i < fieldsets.length; i++) {
      var btns = fieldsets[i].querySelectorAll('.mw-lace-help');
      for (var j = 1; j < btns.length; j++) btns[j].remove();
    }
  }

  // =========================================================================
  // VARIANT CHANGE SURVIVAL
  // =========================================================================

  function isNativeVariantMutation(mutation) {
    var target = mutation && mutation.target;
    if (!target) return false;
    if (target.nodeType !== 1) target = target.parentElement;
    if (!target || !target.closest) return false;

    // SC Options render line-item properties/add-on prices. They are not
    // Shopify variant selectors and must not trigger the Palo Alto variant
    // re-render survival work.
    if (target.closest('.bold_option, .milano-option-row, .personalize-toggle, .mw-review-card, .milano-splitit-fallback')) {
      return false;
    }

    // Ignore our own injected helpers inside native variant UI.
    if (target.closest('.mw-selected-pill, .mw-lace-help, .mw-len-text, .mw-len-num, .mw-len-name')) {
      return false;
    }

    if (target.closest('variant-selects fieldset, variant-selects .selector-wrapper, .product__selectors fieldset, .product__selectors .selector-wrapper')) {
      return true;
    }

    var added = mutation.addedNodes || [];
    for (var i = 0; i < added.length; i++) {
      var node = added[i];
      if (!node || node.nodeType !== 1) continue;
      if (node.closest && node.closest('.bold_option, .milano-option-row, .milano-splitit-fallback')) continue;
      if (node.matches && node.matches('fieldset, .selector-wrapper, variant-selects')) return true;
      if (node.querySelector && node.querySelector('fieldset [data-single-option-selector], .selector-wrapper [data-single-option-selector]')) return true;
    }

    return false;
  }

  function watchVariantChanges() {
    var selectors = document.querySelector('variant-selects, .product__selectors');
    if (!selectors || !selectors.parentElement) return;

    var maxHideTimer = null;

    function clearVariantSettling() {
      if (document.body) document.body.classList.remove('mw-native-variant-settling');
      var dups = document.querySelectorAll('.mw-transient-variant-duplicate');
      for (var d = 0; d < dups.length; d++) dups[d].classList.remove('mw-transient-variant-duplicate');
      try { selectors.style.opacity = '1'; } catch (_) {}
      maxHideTimer = null;
    }

    function isGraceLengthBlock(el) {
      if (!el || el.nodeType !== 1) return false;
      if (el.matches && (el.matches('fieldset.radio__fieldset--variant-option-image') || el.matches('fieldset.mw-length-fieldset'))) return true;
      if (el.querySelector && el.querySelector('input[data-single-option-selector][name="options[Length]"]')) return true;
      var label = el.querySelector && el.querySelector('.radio__legend__label, legend');
      return !!(label && /\blength\b/i.test(label.textContent || ''));
    }

    function suppressTransientDuplicateVariantBlocks() {
      if (!document.body || !document.body.classList.contains('milano-grace')) return;

      var existing = document.querySelectorAll('.mw-transient-variant-duplicate');
      for (var x = 0; x < existing.length; x++) existing[x].classList.remove('mw-transient-variant-duplicate');

      var variantRoots = Array.prototype.slice.call(document.querySelectorAll('.product__form variant-selects, .product__form .product__selectors'));
      var visibleRoots = variantRoots.filter(function(root) { return root && !isNodeDisplayHidden(root); });
      if (visibleRoots.length > 1) {
        // Palo Alto double-buffers variant selectors during viewTransition().
        // Suppress every extra live root immediately so the duplicate Length
        // grid never flashes underneath Lace Front on desktop.
        for (var vr = 1; vr < visibleRoots.length; vr++) visibleRoots[vr].classList.add('mw-transient-variant-duplicate');
      }

      var lengthBlocks = Array.prototype.slice.call(document.querySelectorAll(
        '.product__form fieldset.radio__fieldset--variant-option-image, ' +
        '.product__form fieldset.mw-length-fieldset, ' +
        '.product__form fieldset'
      )).filter(isGraceLengthBlock);

      var visibleLengths = lengthBlocks.filter(function(el) { return el && !isNodeDisplayHidden(el); });
      if (visibleLengths.length > 1) {
        // Prefer the Length block that is already in the correct live position:
        // inside the first visible native selector root and before Cap/Lace.
        var keep = null;
        for (var k = 0; k < visibleLengths.length; k++) {
          var root = visibleLengths[k].closest('variant-selects, .product__selectors');
          if (root && root === visibleRoots[0]) { keep = visibleLengths[k]; break; }
        }
        if (!keep) keep = visibleLengths[0];
        for (var lf = 0; lf < visibleLengths.length; lf++) {
          if (visibleLengths[lf] !== keep) visibleLengths[lf].classList.add('mw-transient-variant-duplicate');
        }
      }
    }

    function markVariantSettling() {
      if (document.body) document.body.classList.add('mw-native-variant-settling');
      suppressTransientDuplicateVariantBlocks();
      if (maxHideTimer) clearTimeout(maxHideTimer);
      // Give DN/SC Options enough time to replace the native amount before the
      // fail-safe reveals it. Resolved prices release the mask earlier from
      // personalize-options.js; this timer only prevents a stuck UI.
      maxHideTimer = setTimeout(clearVariantSettling, 450);
    }

    function hideSelectorsOnMutation() {
      // Do not fade the entire variant area anymore. Fading made Length pills
      // and styling look like they disappeared. Instead, hide only transient
      // duplicate native selector blocks while Palo Alto double-buffers markup.
      markVariantSettling();
    }

    // Start the transient guard on the actual click/change, before Palo Alto's
    // async viewTransition inserts its double-buffered selector markup. This is
    // the earliest safe moment to prevent the desktop duplicate Length flash.
    if (!window._mwNativeSettleGuardBound) {
      window._mwNativeSettleGuardBound = true;
      document.addEventListener('change', function(e) {
        var target = e.target;
        if (!target || !target.closest) return;
        if (!target.matches('input[data-single-option-selector], select[data-single-option-selector]')) return;
        if (target.closest('.bold_option, .milano-option-row')) return;
        if (!target.closest('variant-selects, .product__selectors')) return;
        markVariantSettling();
        // v1.6.47: re-band synchronously with the LENGTH click so a fast ATC
        // can't race the debounced paths. Lace/other native options can't
        // change the band — skip the work inside their click interaction
        // (INP); doVariantWork re-applies after the re-render settles anyway.
        if (target.closest('fieldset.radio__fieldset--variant-option-image') ||
            (target.name || '').toLowerCase() === 'options[length]') {
          applyLengthVisibility();
        }
      }, true);
    }

    function doVariantWork() {
      markVariantSettling();
      reorderCapAndLace();
      cleanupDuplicateGraceHelp();
      addGraceHelpLinks();
      injectLaceSubtitles();
      titleCaseLaceLabels();
      if (window.MILANO_refreshGraceLength) window.MILANO_refreshGraceLength();
      autoSelectCapSize();
      applyLengthVisibility();
      scheduleGraceReorder();
      formatWizardPrices();
      requestPDPPriceSync('wizard');
      suppressTransientDuplicateVariantBlocks();
      if (maxHideTimer) clearTimeout(maxHideTimer);
      maxHideTimer = setTimeout(clearVariantSettling, 450);
    }

    if (!window._mwVariantSelectsReplacedHooked) {
      window._mwVariantSelectsReplacedHooked = true;
      document.addEventListener('milano:variant-selects-replaced', function() {
        doVariantWork();
      });
    }

    if (typeof window.MILANO_onVariantMutation === 'function') {
      window.MILANO_onVariantMutation(hideSelectorsOnMutation, { sync: true, filter: isNativeVariantMutation });
      window.MILANO_onVariantMutation(function() {
        if (document.body.classList.contains('milano-grace')) scheduleGraceReorder();
      }, { sync: true, filter: isNativeVariantMutation });
      window.MILANO_onVariantMutation(doVariantWork, { filter: isNativeVariantMutation });
      return;
    }

    var weAreWriting = false;
    var debounce = null;
    var obs = new MutationObserver(function(mutations) {
      if (weAreWriting) return;
      var nativeMutation = false;
      for (var m = 0; m < mutations.length; m++) {
        if (isNativeVariantMutation(mutations[m])) { nativeMutation = true; break; }
      }
      if (!nativeMutation) return;
      if (debounce) clearTimeout(debounce);
      hideSelectorsOnMutation();
      debounce = setTimeout(function() {
        weAreWriting = true;
        doVariantWork();
        setTimeout(function() { weAreWriting = false; }, 150);
      }, 50);
    });
    obs.observe(selectors.parentElement, { childList: true, subtree: true });
    wiz.observers.push(obs);
  }

  // =========================================================================
  // SKIN → SILK CORRECTION
  // =========================================================================

  function fixSkinToSilk() {
    var targets = document.querySelectorAll(
      '.milano-details-content, .milano-construction-legacy, .milano-specs-table, ' +
      '.milano-product-description, .product__description, [data-product-description]'
    );
    if (targets.length === 0) return;
    var fixed = 0;

    for (var i = 0; i < targets.length; i++) {
      var el = targets[i];
      var html = el.innerHTML;
      var updated = html
        .replace(/\bSkin\s+Part\b/g, 'Silk Part')
        .replace(/\bskin\s+part\b/g, 'silk part')
        .replace(/\bSKIN\s+PART\b/g, 'SILK PART')
        .replace(/\bSkin\s+Top\b/g, 'Silk Top')
        .replace(/\bskin\s+top\b/g, 'silk top')
        .replace(/\bSKIN\s+TOP\b/g, 'SILK TOP')
        .replace(/\bSkin\s+Part\s+Wig\b/g, 'Silk Part Wig')
        .replace(/\bSkin\s+Top\s+Wig\b/g, 'Silk Top Wig')
        .replace(/\bSkin\s+Top\s+Density\b/g, 'Silk Top Density')
        .replace(/\bskin\s+top\s+density\b/gi, 'Silk Top Density');
      if (updated !== html) {
        el.innerHTML = updated;
        fixed++;
      }
    }
  }

  // =========================================================================
  // INJECT ELEMENTS
  // =========================================================================

  function findATCArea() {
    var form = document.querySelector('.product__form');
    return form ? form.querySelector('.product__submit__buttons, .product__submit__holder, .product__submit') : document.querySelector('.product__submit__buttons, .product__submit__holder');
  }

  function injectConsultCTA() {
    var atc = findATCArea();
    if (!atc || document.querySelector('.mw-consult')) return;
    var el = document.createElement('div');
    el.className = 'mw-consult';
    el.innerHTML =
      '<div class="mw-consult__title">Not sure? We\'ll help you choose.</div>' +
      '<p class="mw-consult__text">Our specialists guide women through this every day \u2014 with patience, privacy, and zero pressure.</p>' +
      '<div class="mw-consult__btns">' +
        '<button type="button" class="mw-consult__btn mw-consult__btn--primary" data-mw-open-contact>' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
            '<path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>' +
          '</svg>' +
          '<span>Contact Us</span>' +
        '</button>' +
        '<a href="/pages/consultations" target="_blank" rel="noopener" class="mw-consult__btn mw-consult__btn--secondary">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
            '<rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.6"/>' +
            '<line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
            '<line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
            '<line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" stroke-width="1.6"/>' +
          '</svg>' +
          '<span>Free Consult</span>' +
        '</a>' +
      '</div>';
    atc.parentNode.insertBefore(el, atc.nextSibling);
    // Click handling is delegated page-wide in init() via [data-mw-open-contact]
    // so this consult button and the product-guidance "Contact Us" button share
    // one code path. No per-element binding needed here.
  }

  // =========================================================================
  // CONTACT POPUP
  // =========================================================================

  function buildContactPopup() {
    if (document.getElementById('mw-contact-popup')) return;
    var popup = document.createElement('div');
    popup.id = 'mw-contact-popup';
    popup.className = 'pop-up-container mw-contact-pop';
    popup.style.display = 'none';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.setAttribute('aria-labelledby', 'mw-contact-title');
    popup.innerHTML =
      '<div class="mw-contact-pop__inner">' +
        '<button type="button" class="mw-contact-pop__close" aria-label="Close">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
            '<path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
          '</svg>' +
        '</button>' +
        '<div class="mw-contact-pop__body">' +
          '<div class="mw-contact-pop__icon" aria-hidden="true">' +
            '<svg width="40" height="40" viewBox="0 0 24 24" fill="none">' +
              '<path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
            '</svg>' +
          '</div>' +
          '<h2 id="mw-contact-title" class="mw-contact-pop__title">' +
            'Need help deciding?' +
          '</h2>' +
          '<p class="mw-contact-pop__lead">' +
            'Our wig specialists are here for you.' +
          '</p>' +
          '<div class="mw-contact-pop__btns">' +
            '<a href="tel:+15624262971" class="mw-contact-pop__btn mw-contact-pop__btn--call">' +
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
                '<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.37 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.33 1.85.57 2.81.7A2 2 0 0122 16.92z" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>' +
              '</svg>' +
              '<span>(562) 426-2971</span>' +
            '</a>' +
            '<button type="button" class="mw-contact-pop__btn mw-contact-pop__btn--chat" data-mw-open-chat>' +
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
                '<path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>' +
              '</svg>' +
              '<span>Live Chat</span>' +
            '</button>' +
            '<a href="/pages/consultations" class="mw-contact-pop__btn mw-contact-pop__btn--book">' +
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
                '<rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.7"/>' +
                '<line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>' +
                '<line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>' +
                '<line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" stroke-width="1.7"/>' +
              '</svg>' +
              '<span>Book a Free Consultation</span>' +
            '</a>' +
          '</div>' +
          '<p class="mw-contact-pop__hours">' +
            'Mon\u2013Fri 9am\u20135pm \u00b7 Sun 10am\u20134pm PT' +
          '</p>' +
        '</div>' +
      '</div>';
    document.body.appendChild(popup);

    popup.querySelector('.mw-contact-pop__close').addEventListener('click', closeContactPopup);
    popup.addEventListener('click', function(e) {
      if (e.target === popup) closeContactPopup();
    });

    var chatBtn = popup.querySelector('[data-mw-open-chat]');
    chatBtn.addEventListener('click', function(e) {
      e.preventDefault();
      var opened = false;
      try {
        if (window.GorgiasChat && typeof window.GorgiasChat.open === 'function') {
          window.GorgiasChat.open(); opened = true;
        } else if (window.gorgiasChat && typeof window.gorgiasChat.open === 'function') {
          window.gorgiasChat.open(); opened = true;
        }
      } catch (err) { /* fall through */ }
      if (opened) {
        closeContactPopup();
      } else {
        window.location.href = '/pages/contact-us';
      }
    });
  }

  function openContactPopup() {
    buildContactPopup();
    var popup = document.getElementById('mw-contact-popup');
    if (!popup) return;
    popup.style.display = 'flex';
    document.body.classList.add('mw-popup-open');
    var closeBtn = popup.querySelector('.mw-contact-pop__close');
    if (closeBtn) setTimeout(function() { closeBtn.focus(); }, 50);
    document.addEventListener('keydown', _contactEscHandler);
  }

  function closeContactPopup() {
    var popup = document.getElementById('mw-contact-popup');
    if (!popup) return;
    popup.style.display = 'none';
    document.body.classList.remove('mw-popup-open');
    document.removeEventListener('keydown', _contactEscHandler);
  }

  function _contactEscHandler(e) {
    if (e.key === 'Escape' || e.key === 'Esc') closeContactPopup();
  }

  // =========================================================================
  // CLEANUP
  // =========================================================================

  function cleanup() {
    document.body.classList.remove('milano-wizard-mode');
    document.body.classList.remove('milano-flat-options');
    document.body.classList.remove('mw-popup-open');
    var flatStyles = document.getElementById('mw-flat-styles');
    if (flatStyles) flatStyles.remove();
    var mobileStyles = document.getElementById('mw-mobile-styles');
    if (mobileStyles) mobileStyles.remove();
    document.removeEventListener('keydown', _contactEscHandler);
    var contactPopup = document.getElementById('mw-contact-popup');
    if (contactPopup && contactPopup.parentNode) contactPopup.parentNode.removeChild(contactPopup);
    wiz.active = false;
    for (var k = 0; k < wiz.intervals.length; k++) {
      if (typeof wiz.intervals[k] === 'object' && wiz.intervals[k].stop) wiz.intervals[k].stop();
      else clearInterval(wiz.intervals[k]);
    }
    wiz.intervals = [];
    for (var o = 0; o < wiz.observers.length; o++) wiz.observers[o].disconnect();
    wiz.observers = [];
    var rows = document.querySelectorAll('.milano-option-row');
    for (var i = 0; i < rows.length; i++) rows[i].style.display = '';
    // v1.6.42: dropped .mw-step-notice from sweep list (element removed).
    var injected = document.querySelectorAll('.mw-indicator-row, .mw-hint-wrap, .mw-nav, .mw-consult, .mw-opt-inline-desc, .mw-lace-sub, .mw-lace-help, .mw-inline-help, .mw-badge, .mw-included, .mw-review-card, .mw-toggle-total, .mw-wiz-total, .mw-price-context, .mw-cap-helper, .mw-subgate-label, .mw-section-title, .mw-color-sep, .mw-cut-sep, .mw-style-sep, .mw-entry-choice-title, .mw-segmented, .mw-entry-required, .mw-personalize-desc, .mw-no-cut-virtual, .mw-no-color-virtual, .mw-step-notice');
    for (var j = 0; j < injected.length; j++) if (injected[j].parentNode) injected[j].parentNode.removeChild(injected[j]);
    var priceEnhanced = document.querySelectorAll('.mw-price-enhanced');
    for (var pe = 0; pe < priceEnhanced.length; pe++) priceEnhanced[pe].classList.remove('mw-price-enhanced');
    var relocated = document.querySelector('.mw-size-guide-native');
    var origBlock = document.querySelector('.mw-popup-relocated');
    if (relocated && origBlock) {
      relocated.classList.remove('mw-size-guide-native');
      origBlock.appendChild(relocated);
      origBlock.classList.remove('mw-popup-relocated');
    }
    var ncs = document.querySelectorAll('.mw-no-color');
    for (var n = 0; n < ncs.length; n++) ncs[n].classList.remove('mw-no-color');
    var ncuts = document.querySelectorAll('.mw-no-cut');
    for (var nc = 0; nc < ncuts.length; nc++) ncuts[nc].classList.remove('mw-no-cut');
    var nstyles = document.querySelectorAll('.mw-no-style');
    for (var ns = 0; ns < nstyles.length; ns++) nstyles[ns].classList.remove('mw-no-style');
    var nears = document.querySelectorAll('.mw-no-ear');
    for (var ne = 0; ne < nears.length; ne++) nears[ne].classList.remove('mw-no-ear');
    var earMarked = document.querySelectorAll('[data-mw-ear]');
    for (var em = 0; em < earMarked.length; em++) earMarked[em].removeAttribute('data-mw-ear');
    var hlMarked = document.querySelectorAll('[data-mw-hairline]');
    for (var hm = 0; hm < hlMarked.length; hm++) hlMarked[hm].removeAttribute('data-mw-hairline');
    var groupSeps = document.querySelectorAll('.mw-step-group-sep');
    for (var gs = 0; gs < groupSeps.length; gs++) groupSeps[gs].classList.remove('mw-step-group-sep');
    var animEnters = document.querySelectorAll('.mw-anim-enter');
    for (var ae = 0; ae < animEnters.length; ae++) animEnters[ae].classList.remove('mw-anim-enter');
    var addonHeaders = document.querySelectorAll('.mw-addon-header');
    for (var ah = 0; ah < addonHeaders.length; ah++) if (addonHeaders[ah].parentNode) addonHeaders[ah].parentNode.removeChild(addonHeaders[ah]);
    var addonMarked = document.querySelectorAll('[data-mw-addon-header]');
    for (var am = 0; am < addonMarked.length; am++) addonMarked[am].removeAttribute('data-mw-addon-header');
    var sorted = document.querySelectorAll('[data-mw-color-sorted]');
    for (var cs = 0; cs < sorted.length; cs++) sorted[cs].removeAttribute('data-mw-color-sorted');
    var sgs = document.querySelectorAll('.mw-subgate');
    for (var g = 0; g < sgs.length; g++) sgs[g].classList.remove('mw-subgate');
    var laceGroups = document.querySelectorAll('.mw-lace-equal');
    for (var lg = 0; lg < laceGroups.length; lg++) laceGroups[lg].classList.remove('mw-lace-equal');
    var renamed = document.querySelectorAll('[data-mw-renamed]');
    for (var rm = 0; rm < renamed.length; rm++) renamed[rm].removeAttribute('data-mw-renamed');
    var hasServices = document.querySelector('.mw-has-services');
    if (hasServices) hasServices.classList.remove('mw-has-services');
    wiz.collectionSignal = '';
    wiz.virtualCutSelected = false;
    wiz.virtualColorSelected = false;
    wiz.personalizeDefaultsApplied = false;
    wiz.segmentedEl = null;
    wiz.personalizeDescEl = null;
  }

  // =========================================================================
  // BOOT
  // =========================================================================
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // v2026-06-08: bfcache (back/forward) safety-net. On a restored page,
  // DOMContentLoaded does NOT fire, so init() won't re-run — and an app can
  // re-render the variant selector on restore, dropping our Grace styling hooks
  // (the Length grid then collapses to a 1-column stack until the next nav).
  // Re-assert the styling classes + re-mark the grid WITHOUT re-running
  // transform() (which would attach duplicate change listeners). Idempotent.
  window.addEventListener('pageshow', function(e) {
    if (!e || !e.persisted) return;
    try {
      if (document.body && document.body.classList.contains('milano-grace')) {
        document.body.classList.add('milano-wizard-ready');
        markGraceLengthGrid();
        addGraceHelpLinks();
        // v1.6.47: bfcache restores FORM STATE — a checkbox cleared before
        // navigating away can come back checked inside a length-hidden row.
        applyLengthVisibility();
      }
      // bfcache restores the form (add-ons) but not our rendered recap. Re-render it
      // shortly after restore — by then the price-sync (personalize-options.js pageshow)
      // has re-read the restored selections — so the wig summary shows the right total.
      if (wiz && wiz.reviewed) {
        setTimeout(function () { try { updateReviewCard(); } catch (e2) {} }, 400);
      }
    } catch (err) {
      if (window.MILANO_DEBUG) console.warn('[Milano] pageshow re-assert failed', err);
    }
  });

  setTimeout(fixSkinToSilk, 1500);
})();
